import type { MenuItemType, MenuSetting } from '@/lib/types';

/**
 * 메뉴 골격. v1 `lib/menuConfig.ts` 를 옮긴 것으로 라우트 경로는 v1 과 동일하게 유지한다.
 * 사내 사용자가 즐겨찾기·공유해 둔 URL 이 v2 전환 후에도 그대로 열려야 하기 때문이다.
 */

export interface MenuConfigItem {
  menuKey: string;
  label: string;
  groupKey: string | null;
  sortOrder: number;
  itemType: MenuItemType;
  href?: string;
  /**
   * 이 항목을 열어 주는 <b>대체 메뉴 키</b>. 하나라도 볼 수 있으면 항목이 보인다.
   *
   * 여러 화면을 한 화면의 탭으로 합칠 때 쓴다(주간업무·월간업무). 합쳤다고 권한까지 새로 만들면
   * 이미 부여해 둔 권한이 전부 무효가 되고 관리자가 사람마다 다시 켜야 한다. 탭 안쪽의 판정은
   * 여전히 원래 키(`meetings_hq`·`monthly_hq` …)가 하고, 이 목록은 "그중 하나라도 있으면 입구를 연다" 는 뜻이다.
   */
  visibleWhenAnyOf?: string[];
  /**
   * 라벨 말고도 이 항목을 <b>검색으로 찾게 해 주는 말</b>. 사이드바 검색이 라벨·상위 이름과 함께 본다.
   *
   * v1 이름(`주간회의`·`월간회의`)이 여기 들어 있다. 화면을 합치며 그룹 이름이 「업무 일정」 하나가 되었는데,
   * 옛 이름을 치는 사람이 곧 길을 잃은 사람이다 — 그 사람에게 빈 결과를 돌려주면 안 된다.
   */
  searchAliases?: string[];
  /**
   * <b>그 자리가 열려 있을 때만</b> 검색에 걸리는 말 (사용자 지적 2026-08-28: "조회 권한이 없으면
   * 검색해도 나오면 안 되지").
   *
   * <h2>왜 따로 두나</h2>
   * 사이드바 검색은 권한·게이트가 이미 걸린 트리만 훑으므로 닫힌 <b>메뉴</b>는 나오지 않는다.
   * 그런데 {@link searchAliases} 는 그 규칙을 <b>우회한다</b> — 한 화면 안의 보기·탭 이름을 별칭으로
   * 얹으면, 그 보기가 닫혀 있어도 <b>열려 있는 부모 줄</b>이 검색에 걸린다. 「조직도」를 쳐서
   * 인력배치표로 갔는데 조직도가 없는 상태가 그것이다.
   *
   * <p>그래서 별칭에 <b>자기 게이트 키</b>를 달아 둔다. 그 키가 닫혀 있으면 이 말로는 안 걸리고,
   * 오픈하는 날 저절로 걸리기 시작한다 — 오픈 체크리스트에 한 줄을 더하지 않아도 된다.
   */
  gatedSearchAliases?: Array<{ alias: string; gateKey: string }>;
}

/**
 * 「휴무 관리 › 계획서」 를 <b>임시로</b> 대신하는 바깥 주소 (사용자 지시 2026-08-31).
 *
 * <h2>★ 이제 이 값은 「기본값」 이다 (사용자 지시 2026-09-01)</h2>
 * 주소를 바꾸는 자리는 <b>「환경설정 › 외부 API 설정」 화면</b>으로 옮겼다(백엔드 {@code ExternalLink}).
 * 주소가 바뀔 때마다 이 상수를 고치고 빌드해 올리면, 바꾸는 일이 전산 담당의 일이 되고 그 절차의
 * 길이가 곧 <b>메뉴가 엉뚱한 곳을 가리키고 있는 시간</b>이기 때문이다.
 *
 * <p>그래도 <b>지우지 않는다.</b> 서버 응답(`useExternalLinks`)이 오기 전 한 순간을 이 값이 메운다 —
 * 없으면 그동안 링크가 빈 곳을 가리킨다. 서버 값이 오면 그쪽이 이기고, 화면에서 <b>비워서 저장</b>하면
 * 여기로 되돌아온다. 백엔드 {@code ExternalLink.LEAVE_PLAN} 의 기본값과 <b>같은 글자</b>여야 한다.
 *
 * <p>사이드바 항목과 라우트({@code /hr/leave/plan})가 함께 이 주소를 본다. 두 곳이 어긋나면
 * 메뉴로 간 사람과 주소로 들어온 사람이 서로 다른 화면을 보게 되므로 값은 여기 <b>하나</b>다.
 *
 * <h2>되돌리는 법 — 주석 다섯 줄을 푸는 것이 전부다</h2>
 * 원래 줄은 <b>지우지 않고 바로 아래에 주석으로</b> 남겼다. 사내 화면(`LeavePlanPanel`)도, 그 화면이
 * 쓰는 훅·API 도 손대지 않았으므로 되살리는 데 새로 쓸 코드가 없다.
 * <ol>
 *   <li>{@code menuConfig} — `hr_leave_plan` 줄(주석 처리된 `link` 판으로 되돌린다)</li>
 *   <li>{@code screens.tsx} — `/hr/leave` 의 이동 대상, `/hr/leave/plan` 의 element</li>
 *   <li>{@code LeaveManagementPage} — 탭 한 줄 · 머리글 한 줄 · 그리는 가지 · import</li>
 * </ol>
 *
 * <p><b>키와 게이트는 되돌리지 않아도 된다.</b> 되돌린 뒤에도 `hr_leave_plan` 은 그대로 살아 있고
 * (`visibleWhenAnyOf` 가 부모 키를 다시 물어 준다), 라우트의 `menuKey` 도 두 상태 모두에서 맞다.
 * 대시보드·현황을 다시 열 때는 <b>오픈 관리 모달에서 그 두 줄을 켜면</b> 된다 — V174 는 처음 값만
 * 정했고, 마이그레이션을 새로 쓸 일이 아니다.
 *
 * <p>서버 쪽에 남는 것은 「자리로 연다」 는 판정 하나다({@code UserMenuPermissionResolver}). 그것은
 * 이 링크의 사정이 아니라 <b>휴무 관리 자체의 규칙</b>이라, 링크를 걷어도 그대로 맞다.
 */
export const LEAVE_PLAN_EXTERNAL_URL =
  'https://chaneul88.vercel.app/%EB%B3%B4%EB%AF%B8%EA%B1%B4%EC%84%A4_2026%EB%85%84_9%EC%9B%94_%ED%98%84%EC%9E%A5%EB%B3%84_%ED%9C%B4%EB%AC%B4%EA%B3%84%ED%9A%8D_%EB%8C%80%EC%8B%9C%EB%B3%B4%EB%93%9C(1).html';

/**
 * 메뉴 키 → <b>외부 링크 코드</b> (백엔드 {@code ExternalLink.code()} · 사용자 지시 2026-09-01).
 *
 * <p>여기 든 항목은 {@code href} 를 <b>설정 값으로 갈아 끼운 뒤</b> 그려진다({@code useMenuTree}).
 * 골격의 {@code href} 는 그대로 두는데, 그것이 설정이 비었을 때의 기본값이기 때문이다.
 *
 * <p>바깥 화면을 하나 더 붙이는 날 하는 일은 <b>백엔드 열거형에 한 줄, 여기 한 줄</b>이다.
 */
export const MENU_EXTERNAL_LINKS: Record<string, string> = {
  hr_leave_plan: 'leave_plan',
};

/**
 * 바깥 웹메일 주소 — 백엔드 {@code ExternalLink.MAILBOX} 와 <b>같은 글자</b>. 환경설정 › 외부 링크의
 * 기본값이고, <b>지금은 어느 메뉴도 이 값으로 나가지 않는다</b>.
 *
 * <h2>메일함은 우리가 만들지 않는다 (확정 2026-09-21 · DOC-88)</h2>
 * 두 번 검토했고 두 번 같은 벽에 막혔다. 카페24도(2026-09-15) 가비아·하이웍스도(2026-09-21 고객센터
 * 답변) <b>수신 IMAP 을 주지 않는다</b> — 외부 연동은 POP3 뿐이고, 메일 API 도 발송 전용이다.
 *
 * <p>POP3 에는 폴더도 읽음 상태도 없다. 그 상태를 메일 서버가 안 들면 우리가 들어야 하고, 그러려면
 * <b>250명의 메일을 우리 DB 에 복제</b>하게 된다. 업체는 용량이 무제한이고 우리는 아니다 —
 * 무제한을 유한한 곳에 베끼면서 백업·개인정보 책임만 늘고, 그러고도 저쪽 웹메일보다 나은 점이 없다.
 * POP3 는 받은편지함만 주어 <b>지난 메일 찾기</b>부터 처음부터 못 한다(사용자 판단 2026-09-21).
 *
 * <p>이 상수는 남긴다. 웹메일로 나가는 문이 다시 필요해지는 날 외부 링크 설정이 그 값을 쓴다.
 */
export const MAILBOX_EXTERNAL_URL = 'https://webmail.bomienc.com/';

/**
 * 사이드바에서 <b>물러난</b> 키. 화면 통합(업무 일정)으로 골격에서 빠졌지만 권한 키로는 살아 있다.
 *
 * 골격에 없는 키가 서버 설정(`menu_settings`)에 남아 있으면 {@link mergeMenuConfig} 가 "관리자가 추가한 메뉴"로
 * 착각해 이동할 수 없는 항목을 사이드바에 그린다. 예전에 라벨을 고쳐 둔 회사일수록 그 찌꺼기가 많으므로
 * 여기 적어 두고 걸러 낸다.
 */
export const RETIRED_MENU_KEYS = new Set<string>([
  // v1 의 두 그룹(월간회의·주간회의). 그 아래 화면들은 사이드바로 돌아왔지만(DOC-24) 그룹은 돌아오지 않는다 —
  // 「업무 일정」 한 그룹 아래 주간업무·월간업무가 서고, v1 이름은 검색어(`searchAliases`)로만 남는다.
  'monthly',
  'meetings',
  // 통합 캘린더(`calendar`)는 여기 없다 — 월간업무의 '전체' 하위 항목으로 골격에 돌아왔다(2026-10-01).
  // 공정관리 그룹과 그 아래 외부 링크(공정표 작성 → scheide.netlify.app). 골격에서는 내렸지만(2026-08-11)
  // v1 `menu_settings` 를 그대로 옮겨 온 서버 설정에 행이 남아 있어 "관리자가 추가한 메뉴" 로 되살아났다.
  // 공정표는 현장 조회 공정율 탭 하나로 모은다(DOC-25 §7.5).
  'process',
  'process_write',
  // 도면 뷰어. 자료 조회가 뷰어를 겸하게 되면서 골격에서 내렸다(2026-08-18 · DOC-41 §9).
  // ★ 권한 키로는 살아 있다 — DWG 변환 API 가 그 키를 쓰고, 그랜트를 지우면 되돌릴 때 다시 나눠 줘야 한다.
  //   이 목록에 넣지 않으면 서버 `menu_settings` 의 남은 행이 "관리자가 추가한 메뉴" 로 되살아나,
  //   눌러도 아무 데도 가지 않는 「도면 뷰어」가 사이드바에 남는다.
  'bim_viewer',
  // AI 경력매치(2026-09-23). 묻는 자리가 AI Agent 대화로 옮겨 가 골격에서 줄을 내렸다.
  // ★ 권한 키로는 살아 있다 — 대화 도구(`match_careers`)가 부르는 문을 그 키가 막는다.
  //   이 목록에 넣지 않으면 서버 `menu_settings` 의 남은 행이 "관리자가 추가한 메뉴" 로
  //   되살아나, 눌러도 대화가 대신해 주는 화면이 사이드바에 다시 선다.
  'hr_search',
  // 주차 관리(v1 `/settings/weeks`). v2 는 주차를 사람이 등록하지 않는다 — `WeekResolver.ensureWindow` 가
  // 필요한 주차를 스스로 만든다(scripts/v1-to-v2/02_map_to_v2.sql §2-1). 골격에 없는 키라
  // v1 덤프의 `menu_settings` 행이 "관리자가 추가한 메뉴" 로 되살아나, href 없는 항목이 환경설정 아래에서
  // 눌릴 때마다 하위 항목이 있는 것처럼 펼쳐졌다 접혔다 한다.
  'settings_weeks',
  /*
    보미소식 그룹(2026-09-18 · 사용자 지시). 아래 일곱 줄 가운데 다섯은 <b>게시판</b>으로 옮겼고
    (사내지침&매뉴얼·입/퇴사자 소식·투표·건설동향·뉴스), 식당안내는 홈 위젯이 대신한다.
    남은 것은 공지사항 하나라, 한 줄을 펴야 한 줄이 나오는 그룹이 되었다 —
    {@code news_notices} 가 <b>최상위 링크</b>로 올라서고 이 그룹 키는 물러난다.

    ★ 권한 키는 하나도 손대지 않았다. 옮긴 다섯 줄은 자기 키를 그대로 들고 갔고,
      {@code news} 는 애초에 그룹 머리라 아무 권한도 걸려 있지 않았다.
  */
  'news',
  /*
    식당안내(2026-09-18 · 사용자 지시). <b>메뉴에서만</b> 내렸다 — 화면(`/news/dining`)도
    라우트도 그대로 있고, 모바일 앱이 그 주소를 계속 쓴다. 보는·고치는 자리는 홈 위젯
    「주단위 식단표」의 연필이다.

    ★ 권한 키 {@code news_dining} 은 살아 있다 — 위젯의 조회·저장 API 를 그 키가 막는다.
      사이드바에 줄이 없어졌으므로 권한 매트릭스에서는 공지사항 밑에 매단다
      ({@code permissionTree} 의 `ATTACHED_KEYS`).
  */
  'news_dining',
  /*
    뉴스·건설동향(2026-09-18 · 사용자 지시). 식당안내와 <b>같은 판단</b>이다 — 화면도 라우트도
    그대로 두고 사이드바 줄만 내렸다. 들어가는 문은 홈 위젯 「뉴스·건설동향」의 <b>전체보기</b>다.

    ★ 두 위젯을 하나로 합쳤다. 건설동향 위젯은 기본이 꺼짐이라, 메뉴를 내리면 그것을 켠 적 없는
      사람에게는 <b>들어갈 길이 하나도 안 남는다</b>(사이드바 검색도 메뉴 줄이 없으면 안 걸린다).
      기본으로 켜져 있는 뉴스 위젯 안의 토글로 옮겨, 입구가 사라지는 사람이 없게 했다.

    ★ 권한 키 둘(`news_trends`·`news_headlines`)은 살아 있다 — 위젯과 화면의 API 를 그 키가 막고,
      수집 설정(`news_curation`)도 거기 매달려 있다. 권한 매트릭스에서는 공지사항 밑으로 옮겼다
      ({@code permissionTree} 의 `ATTACHED_KEYS`).
  */
  'news_trends',
  'news_headlines',
  /*
    개선요청(2026-09-22 · 사용자 지시). <b>메뉴에서만</b> 내렸다 — 화면(`/requests`)도 라우트도 그대로
    있고, 들어가는 문은 헤더의 빨간 「개선사항 요청」 버튼이다. 창을 열던 그 버튼이 이제 이 화면으로
    간다({@link Navigation}).

    ★ 권한 키 {@code requests} 는 살아 있다 — 게시판 API 를 그 키가 막는다(백엔드
      {@code RequestBoardController} 의 MENU="requests"). 권한 매트릭스에서는 게시판 밑에 매단다.
  */
  'requests',
  /*
    공종별 기술집·신기술 소개(2026-09-22 · 사용자 지시). 사이드바 줄만 걷었다 — 둘 다 이제
    보미기술집 화면 <b>안의 탭</b>이고 옛 주소도 그대로 열린다({@code TechCollectionPage}).

    ★ 권한 키 둘은 살아 있다. 탭이 그 키로 갈리고(`useMenuCapability`), 글을 쓰는 API 도 같은 키를 본다.
  */
  'tech_cases',
  'tech_new',
  /*
    보미 실적(2026-09-22 · 사용자 지시). 이쪽은 <b>화면째</b> 옮겼다 — 현장 조회의 준공 탭 안으로
    들어갔다({@code PerformancePanel}). 남은 줄도 라우트도 없다.

    ★ 권한 키 {@code tech_performance} 는 목록·삭제 API 가 아직 쓴다. 준공 탭 안의 실적은 그 탭과
      같은 키({@code site_pmis})로 갈리므로, 이 키는 「실적을 통째로 훑고 지우는 일」 만 남는다.
  */
  'tech_performance',
  /*
    전자결재(2026-09-22 · 사용자 지시: 「좌측 전자결재 메뉴를 없애고 이거로 통일할거라」).
    <b>메뉴에서만</b> 내렸다 — 화면도 라우트도 그대로 있고(`/approval` · `/approval/my` 둘 다),
    들어가는 문은 헤더의 「전자결재」 버튼이다. 결재함과 기안 서식이 그 화면 하나에 함께 선다.

    ★ 권한 키 {@code approval} 은 살아 있다 — 결재 API 전부가 그 키를 본다. 헤더 버튼도 그 키로
      갈리고({@link Navigation}), 오픈 게이트에서는 「상단 헤더」 묶음에 둔다
      ({@link GATE_EXTRA_SECTIONS}). 권한 매트릭스에서는 업무 일정 밑에 매단다 — 사이드바에서
      바로 그 자리(업무 일정 다음)에 서 있던 줄이다.
  */
  'approval',
  /*
    경력관리(2026-09-23 · 사용자 지시: 「경력관리와 인사자료 관리를 하나로 합치고 상세·수정을
    탭으로 나눈다」). 사이드바 줄만 걷었다 — 화면은 <b>인사자료</b> 안의 「경력」 탭이 되었고
    (`HrRecordsPage` · `CareerPanel`), 옛 주소 `/hr/management` 는 그대로 살아 그 자리로 보낸다.

    ★ 권한 키 {@code hr_management} 는 살아 있다. 경력·기술등급·인력 저장 API 가 전부 이 키를 보고
      (백엔드 {@code CareerController}·{@code TechGradeController}·{@code PersonnelController}),
      「경력」 탭도 이 키로 갈린다(`useMenuCapability`). <b>키를 지우면 경력관리만 받던 사람이
      급여·고과까지 열리거나 아무것도 못 보거나 둘 중 하나가 된다</b> — 그래서 합친 것은 줄이지
      키가 아니다.

    ★ 여기 적지 않으면 서버 `menu_settings` 에 남은 v1 행이 "관리자가 추가한 메뉴" 로 되살아나
      (`mergeMenuConfig`), 눌러도 아무 데도 가지 않는 「경력관리」가 인사관리 아래에 남는다.
      권한 매트릭스에서는 인사자료 밑에 매단다({@code permissionTree} 의 `ATTACHED_KEYS`).
  */
  'hr_management',
  /*
    인사자료 › 연차관리(2026-10-26 · 사용자 지시 「출결과 연차를 하나로 묶어서」). 표는 근태 관리의 「연차관리」
    탭이 됐고 옛 주소는 그 탭으로 넘어간다. 이 줄은 권한 키가 아니었다(`visibleWhenAnyOf: hr_records`) —
    남은 `menu_settings`·게이트 행이 사이드바에 되살아나지 않게 여기 적는다.
  */
  'hr_records_promotion',
  /*
    안전 관리(2026-10-27 · 사용자 지시 「파트너 페이지처럼 안전 홈페이지로」). 그룹째 사이드바에서 걷었다 —
    화면은 `safe/` 앱(safe.bomienc.com)으로 옮겼고, 옛 주소는 라우터가 그리로 넘긴다(`lib/safetyHome.ts`).

    ★ 권한 키 {@code safety} 는 살아 있다 — 안전 API 전부(`SafetyJournalController.MENU`)와 안전 홈페이지의
      입장이 그 키를 본다. 오픈 게이트는 아래 {@link GATE_EXTRA_SECTIONS} 의 「안전 홈페이지」, 권한 매트릭스는
      현장 현황 밑({@code permissionTree} 의 `ATTACHED_KEYS`)이다. 아래 넷은 권한이 아니라 화면 이름이었다.
  */
  'safety',
  'safety_dashboard',
  'safety_education_log',
  'safety_health_log',
  'safety_patrol_log',
]);

/**
 * 관리자가 메뉴 관리 화면에서 바꿔도 구조(소속 그룹·종류)를 되돌리지 않는 키.
 * 라벨만 바꿀 수 있다.
 */
export const LOCKED_MENU_STRUCTURE_KEYS = [
  // 현장 현황 셋 — 입찰·진행·준공은 <b>현장의 흐름</b>이다. 다른 그룹으로 흩어지면
  // 「준공한 현장의 CS」가 현장과 상관없는 자리에 서게 된다(사용자 지시 2026-09-29).
  'site_status_active',
  'site_status_bid',
  'site_status_completed',
  // 입찰 아래 갈래 둘(2026-09-30). 입찰 밖으로 나가면 「수주」와 「발주」라는 이름만 남아,
  // 무엇을 수주하고 무엇을 발주하는지가 사라진다 — 이름이 뜻을 갖는 것은 부모 아래일 때다.
  'site_status_bid_tender',
  'site_pmis_rfq',
  // 업무 일정 — 대시보드·주간업무·월간업무가 한 그룹이어야 한다. 흩어지면 다시 달력이 네 개가 된다.
  'work',
  'dashboard',
  'work_weekly',
  'work_monthly',
  // v1 회의 화면들. 주간업무·월간업무 아래 하위 항목으로 되돌아왔다(DOC-24). 라벨은 바꿔도 소속은 고정이다 —
  // 다른 그룹으로 흩어지면 통합 화면과 하위 항목이 서로 다른 곳을 가리키게 된다.
  'meetings_hq',
  'meetings_site',
  'meetings_completed',
  'monthly_hq',
  'monthly_field',
  'monthly_site',
  'calendar',
  // 공지사항은 최상위 링크다(2026-09-18). 나머지 넷은 게시판 아래로 옮겼고, 소속을 고정해 둔다 —
  // v1 덤프의 `menu_settings` 에는 이 넷이 아직 `news` 아래로 적혀 있어서, 풀어 두면 다음 sync 때
  // 사라진 그룹 밑으로 되돌아가 사이드바에서 통째로 안 보인다.
  'news_notices',
  'news_polls',
  'news_employee',
  'news_guidelines',
  // 보미기술집은 최상위 링크다(2026-09-22). 하위 셋이 없어졌으므로 이 키 하나만 고정한다 —
  // v1 덤프의 `menu_settings` 에는 아직 그룹으로 적혀 있어, 풀어 두면 다음 sync 때 그룹으로 되돌아가
  // 눌러도 아무 데도 가지 않는 줄이 된다.
  'tech',
  // 자료실 — 문서 분류·자료 조회가 한 골격으로 묶여야 한다.
  'docs',
  'docs_categories',
  'docs_browse',
  'docs_submissions',
  'docs_lab',
  // 게시판 그룹과 그 하위 둘. 익명 제보가 다른 그룹으로 흩어지면 「여기 쓴 글은 익명」 이라는
  // 경계가 흐려진다 — 이름이 곧 규칙인 자리라 소속을 고정한다.
  //
  // 개선요청 줄은 2026-09-22 에 사이드바에서 내려갔다(위 `RETIRED_MENU_KEYS`) — 헤더 버튼이
  // 그 화면으로 간다. 골격에 없는 키라 여기서도 뺀다.
  'board',
  'board_thanks',
  // 자산관리 — 「예약·신청」과 「자산 관리」 두 갈래. 화면이 늘면 둘 중 한쪽 아래로 붙는다.
  'assets',
  'asset_requests',
  'asset_registry',
  'asset_supplies',
  // 총무 관리 — 두 부서만 보는 그룹(DOC-120). 다른 그룹으로 옮겨지면 「두 부서만」 이라는 경계가 흐려진다.
  'general_affairs',
  // 원격지원(DOC-137) — 남의 PC 에 들어가는 자리다. 다른 그룹 아래로 흩어지면 권한 매트릭스에서 찾기 어려워진다.
  'remote_support',
  // 전자결재는 2026-09-22 에 사이드바에서 내려갔다(위 `RETIRED_MENU_KEYS`) — 헤더 버튼이
  // 그 화면으로 간다. 골격에 없는 키라 여기서도 뺀다.
  // 나머지 최상위 셋. 순서를 고정한 이상 <b>자리</b>도 고정해야 한다 — v1 덤프가 이 키들을 다른 그룹
  // 아래로 옮겨 놓으면 최상위에서 아예 사라지고, 그러면 고정한 순서에 빈칸이 생긴다.
  'site_status',
  'hr',
  'settings',
] as const;

/**
 * 최고 관리자(super_admin)에게만 열리는 <b>SPA 화면</b> 키. 라우트 가드({@code RequireAuth})가
 * 이 집합으로 직접 진입을 되돌린다.
 *
 * <h2>지금은 비어 있다 (사용자 지시 2026-09-01)</h2>
 * 관리자 대시보드가 여기 있었다. 서버 상태·트래픽·오류를 보는 화면인데, <b>그것을 보고 조치하는
 * 사람이 곧 관리자</b>라 최고 관리자만 열어 두면 「장애를 아는 사람」과 「고칠 수 있는 사람」이
 * 갈린다. 그래서 admin 에게도 연다(백엔드 {@code AdminDashboardController} 도 ADMIN 으로 맞췄다).
 *
 * <p><b>DB 운영 콘솔(`/ops`)은 여기 들지 않는다.</b> 그쪽은 이 SPA 의 라우트가 아니라 agent 가
 * 띄우는 별도 창이고, 자격 판정도 그 창이 스스로 한다({@code agent/app/ops/auth.py} — SUPER_ADMIN
 * 또는 {@code settings_ops} 지목). 이 집합에 넣어 봐야 아무 문도 잠그지 못한다.
 *
 * <p>비어 있어도 지우지 않는다 — 「super_admin 만 여는 화면」 을 다시 만들 때 판정을 어디에
 * 적을지 다시 정하게 되고, 그때 라우트 가드가 아닌 자리에 적히면 화면만 열리고 API 는 막히는
 * 어긋남이 생긴다.
 *
 * <h2>오류 기록(2026-10-07 · V406)</h2>
 * 낱건에 <b>누가 언제 어느 주소에서</b> 무엇을 겪었는지가 이름과 함께 남는다 — 사용자가 최고
 * 관리자만 보기로 정했다. 백엔드도 {@code ErrorLogController} 가 SUPER_ADMIN 으로 막는다.
 * SUPER_ADMIN 은 역할이 아니라 계정 종류라 권한 매트릭스({@code permissionGroups})에 줄을 두지
 * 않는다 — 두면 역할에 줘도 열리지 않는 칸이 선다.
 *
 * <h2>변경이력은 여기서 뺐다 (2026-10-30 · V603)</h2>
 * 처음(V448)엔 최고 관리자 전용이었는데, 「특정 인원 열어 주자」 로 역할 그랜트로 주게 바꿨다.
 * 권한 매트릭스에 줄이 서고, 서버는 {@code settings_release_notes} 의 수정권한으로 쓰기를 막는다.
 */
export const SUPER_ADMIN_ONLY_MENU_KEYS = new Set<string>(['settings_error_log']);

/**
 * 사이드바 골격에 없지만 <b>오픈 게이트로는 닫을 수 있는</b> 자리 (V153 · 사용자 지시 2026-08-28).
 *
 * <p>헤더 버튼과 현장 조회의 탭들이다. 어느 쪽도 {@link DEFAULT_MENU_CONFIG} 에 없어서 오픈 관리
 * 모달이 골격만 그려서는 손댈 수 없다 — 이 목록을 트리 뒤에 이어 붙인다.
 *
 * <h2>여기 드는 것과 들지 않는 것</h2>
 * 자기 <b>메뉴 키를 이미 가진</b> 탭은 여기 들지 않는다 — 원가관리({@code site_pmis_office})·
 * 수금관리·하도급·주요자재가 그렇다. 그 키를 게이트에서 닫으면 권한 자체가 내려와 탭도 API 도
 * 함께 닫힌다. 여기 드는 것들(건축허가&amp;착공·준공·현장사진·조직도)은 개요·연락처와 <b>같은 키</b>
 * ({@code site_pmis})를 쓴다: 키로 닫으면 개요까지 함께 닫히므로 탭 전용 이름을 새로 붙인다.
 *
 * ★ 그래서 이 이름들은 <b>화면만</b> 닫는다. 백엔드 권한 표({@code MenuActions})에 없는 키라
 *   서버가 이것으로 API 를 막지는 않는다.
 *
 * <p>{@code hint} 는 <b>화면에 그리지 않는다</b>(사용자 지시 2026-08-28 — 이름 뒤의 설명을 걷었다).
 * 지우지 않고 남기는 것은 「탭만 감추는가, API 까지 닫는가」가 이 목록을 고칠 때 반드시 알아야 하는
 * 구분이기 때문이다 — 주석보다 줄 옆에 붙어 있어야 다음 사람이 빠뜨리지 않는다.
 */
export const GATE_EXTRA_SECTIONS: Array<{
  title: string;
  /** `searchAliases` — 사이드바 줄이 없는 키의 찾기 말(`buildMenuSearchText`). */
  items: Array<{ menuKey: string; label: string; hint: string; searchAliases?: string[] }>;
}> = [
  {
    title: '상단 헤더',
    items: [
      { menuKey: 'ai_agent', label: 'AI Agent', hint: '헤더 버튼 · /aiagent' },
      // ★ 메일함은 여기 없다 — 사이드바에도 헤더에도 줄이 없다(2026-09-18 에 내렸고,
      //   2026-09-21 에 우리 화면으로 만들지 않기로 확정했다 · DOC-88).
      { menuKey: 'admin_dashboard', label: '관리자 대시보드', hint: '헤더 버튼 · 최고 관리자 전용' },
      // 전자결재는 2026-09-22 에 사이드바에서 내려왔다 — 닫을 자리가 여기밖에 없다.
      // 닫으면 헤더 버튼이 사라지고 결재 API 도 함께 닫힌다(진짜 권한 키다).
      { menuKey: 'approval', label: '전자결재', hint: '헤더 버튼 · /approval/my + 결재 API' },
      { menuKey: 'office_dashboard', label: '현장 전체 대시보드', hint: '현장 현황 머리말 버튼 · /office/dashboard' },
    ],
  },
  {
    // 현장 조회는 사이드바에 없다 — 현장 카드를 눌러 들어간다(DOC-12). 그래서 탭이 위 트리에
    // 하나도 나오지 않았고, 모달에서 손댈 수 없는 자리였다. 목차 열 개를 전부 여기 편다.
    title: '현장 조회 탭',
    items: [
      { menuKey: 'site_pmis_overview', label: '개요', hint: '탭만 감춤' },
      { menuKey: 'site_pmis_contract', label: '계약서', hint: '탭 + 계약 API' },
      // 조직도(2026-09-28). 인력배치표의 조직도를 현장 하나로 고정해 세운 탭이라 자료는 배치
      // (`hr_placement`)의 것이지만, 이 탭을 여는 키는 개요와 같은 `site_pmis` 다 — 탭 전용 이름.
      { menuKey: 'site_pmis_org', label: '조직도', hint: '탭만 감춤' },
      { menuKey: 'site_pmis_contacts', label: '연락처', hint: '탭만 감춤' },
      { menuKey: 'site_pmis_checklist', label: '체크리스트', hint: '탭 + 체크리스트 API' },
      { menuKey: 'site_pmis_permit', label: '건축허가&착공', hint: '탭만 감춤' },
      // 공정관리는 2026-09-04 에 진짜 권한 키가 됐다(V237) — 끄면 공정 트리·곡선 API 도 함께 닫힌다.
      { menuKey: 'site_pmis_progress', label: '공정관리', hint: '탭 + 공정 API' },
      { menuKey: 'site_pmis_office', label: '원가관리', hint: '탭 + 원가 API' },
      /*
        ★ <b>견적·입찰(`site_pmis_rfq`)은 여기 없다</b>(2026-09-30). 2026-09-29 하루 서 있었고,
          화면이 현장 현황 › 입찰 › <b>발주</b>로 옮겨 가면서 사이드바 골격
          ({@link DEFAULT_MENU_CONFIG})에 줄이 생겼다 — 오픈 관리 모달이 트리에서 그 줄을 이미
          그리므로, 여기 남겨 두면 <b>같은 키의 스위치가 두 개</b> 서고 한쪽만 눌러도 되는 것처럼
          보인다. 키·게이트 행·권한은 V359 그대로다.
      */
      { menuKey: 'site_pmis_subcontract', label: '하도급관리', hint: '탭 + 하도급 API' },
      { menuKey: 'site_pmis_payment', label: '수금관리', hint: '탭 + 수금 API' },
      { menuKey: 'site_pmis_material', label: '주요자재관리', hint: '탭 + 자재 API' },
      { menuKey: 'site_pmis_completion', label: '준공', hint: '탭만 감춤' },
      // 현장사진(V163 · 2026-08-28 · 이름은 2026-09-28 에 「전경사진」에서 바뀌었다). 개요와 같은
      // 키로 열리므로 탭 전용 이름을 붙였다 — 1차 오픈에서는 꺼 둔다.
      { menuKey: 'site_pmis_scenery', label: '현장사진', hint: '탭만 감춤' },
      // 공사일보(V347 · 2026-09-29). 실험실의 스마트 공사일보가 현장 단위로 옮겨 온 것이다. 개요와 같은
      // 키(site_pmis)로 열리므로 탭 전용 이름을 붙였다. 열어서 낸다(사용자 지시 「일단 켜두자」) —
      // 이 줄은 닫는 손이다.
      { menuKey: 'site_pmis_daily', label: '공사일보', hint: '탭만 감춤' },
      // 현장관리 — 인허가 유효기간 대장(V445 · 2026-10-14). 자기 권한 키라 탭과 API 를 함께 닫는다.
      // 닫은 채로 낸다 — 이 줄이 여는 손이다.
      { menuKey: 'site_pmis_field', label: '현장관리', hint: '탭 + 인허가 API' },
    ],
  },
  {
    /*
      인력배치표는 1차 오픈에서 <b>메뉴로는 열려 있다</b>(인사관리 아래 유일하게 남긴 줄).
      그런데 그 안이 보기 셋으로 갈리고, 그중 둘은 아직 낼 것이 아니다(사용자 지시 2026-08-28).
      메뉴 키는 하나뿐이라(`hr_placement`) 키로는 못 나눈다 — 닫으면 명부까지 함께 사라진다.
    */
    title: '인력배치표 보기',
    items: [
      { menuKey: 'hr_placement_board', label: '전술판', hint: '보기만 감춤 · 명부와 같은 권한' },
      { menuKey: 'hr_placement_chart', label: '조직도', hint: '보기만 감춤 · 명부와 같은 권한' },
      { menuKey: 'hr_placement_seat', label: '자리배치도', hint: '보기만 감춤 · 앉히는 것은 최고관리자·인사총무팀' },
    ],
  },
  {
    /*
      실험실은 메뉴 키 하나(`docs_lab`)에 탭이 여럿이다. 캐드 어시스턴트(DOC-112 · V424)는 닫은 채로
      내고 여기서 켠다(사용자 지시 2026-10-07: 「처음엔 비공개로 하고, 최고 관리자는 이 탭을 열고닫게」).
      키로 닫으면 드론 토공량까지 함께 사라지므로 탭 전용 이름이다 — 자리배치도와 같은 규칙.
    */
    title: '실험실 탭',
    items: [
      // 실험 탭 전부(사용자 지시 2026-10-08). 캐드 어시스턴트만 닫힌 채로 냈고(V424) 나머지는 줄이 없어
      // 열려 있다. 실험실 화면의 탭 옆 스위치(LabTabGateSwitch)와 같은 줄을 민다.
      // 시안 둘은 코드로 최고관리자에게 묶여 있다가 2026-10-08 에 게이트로 왔다(V425 · 닫힌 채 출발).
      { menuKey: 'docs_lab_homepage', label: '홈페이지 시안', hint: '탭만 감춤 · 서버 없음' },
      { menuKey: 'docs_lab_design', label: '신규 디자인', hint: '탭만 감춤 · 서버 없음' },
      { menuKey: 'docs_lab_earthwork', label: '드론 토공량', hint: '탭만 감춤' },
      { menuKey: 'docs_lab_excavator', label: '굴착기 시뮬레이션', hint: '탭만 감춤' },
      { menuKey: 'docs_lab_bohal', label: '보할 적정성 검토', hint: '탭만 감춤' },
      { menuKey: 'docs_lab_hwp', label: '한글(hwp) 읽기', hint: '탭만 감춤' },
      { menuKey: 'docs_lab_dococr', label: '검측 서류 판독', hint: '탭만 감춤' },
      { menuKey: 'docs_lab_apprelease', label: '앱 배포', hint: '탭만 감춤 · 받기는 AppInstallButton 이 따로' },
      { menuKey: 'docs_lab_cad', label: '캐드 어시스턴트', hint: '탭만 감춤 · 받기·후기 API 는 docs_lab 권한이 막음' },
    ],
  },
  {
    /*
      <b>줄이 없어 영영 못 여는 키가 있었다</b> (2026-09-18 · 현장직원에게 자산관리가 통째로
      안 보이던 일).

      `asset_reservations` 는 권한 키일 뿐 사이드바 줄이 아니다 — 차량 예약·회의실 예약 두 줄이
      {@code visibleWhenAnyOf} 로 이 키를 빌려 쓴다. V153 이 이것까지 닫아 두었는데 위 트리에도
      이 목록에도 없어서 <b>모달에 스위치가 서지 않았고</b>, 저장은 서버의 닫힘 집합을 초안으로
      삼아 그린 줄만 고치므로(`MenuGateModal`) 몇 번을 저장해도 닫힌 채로 되돌아갔다.

      그 키가 닫혀 있으면 {@code menuCapabilities} 가 그것을 빼고({@code UserMenuPermissionResolver}),
      `visibleMenuKeys` 에 없으니 예약 두 줄이 권한 필터에서 탈락하고, 자식이 빈 예약·신청 관리와
      자산관리가 차례로 접힌다 — 스위치 넷을 다 켜 놓고도 <b>자산관리 줄 자체가 사라진다</b>.

      ★ 여는 쪽은 {@code MenuGateModal.openKeysUnder} 가 대체 키까지 함께 걷어 준다. 여기 줄을
        세우는 것은 <b>되돌리는 손</b>이다 — 그 길이 없으면 한 번 연 키를 다시 닫을 자리가 없다.
      ★ 나머지 다섯도 같은 처지였다(급여·고과는 인사자료 화면 안의 탭, NAS 는 자료 조회 안의
        설정, 자산 공지는 화면 머리말, BIM 은 도면 뷰어). 서버 권한 표({@code MenuActions})에
        있는 키들이라 닫으면 <b>API 까지</b> 닫힌다.
    */
    title: '줄이 없는 권한 키',
    items: [
      { menuKey: 'asset_reservations', label: '차량·회의실 예약', hint: '예약 두 줄이 이 키로 열린다' },
      { menuKey: 'asset_notices', label: '자산 공지', hint: '자산관리 화면 머리말' },
      // 화면(마이페이지 탭 · 인사자료 안의 탭)은 내렸다(사용자 지시 2026-09-22). 키를 함께 지우지
      // 않은 이유는 <b>급여 API 가 그대로 살아 있기</b> 때문이다 — 닫을 자리가 없어지면 남는 것은
      // 열린 API 뿐이다. 화면을 되살리면 이 키가 그때도 같은 일을 한다.
      // 2026-10-22: 엑셀로 한 달 치를 올리는 화면(/hr/records/payroll · V518)이 이 키로 다시 섰다.
      { menuKey: 'hr_records_payroll', label: '급여명세서', hint: '/hr/records/payroll + 급여 API' },
      { menuKey: 'hr_records_evaluation', label: '인사고과', hint: '인사자료 안의 탭 + 고과 API' },
      { menuKey: 'settings_nas', label: 'NAS 접속 설정', hint: '자료 조회 안의 설정 + NAS API' },
      { menuKey: 'bim_viewer', label: 'BIM 뷰어', hint: '도면 뷰어 + 변환 API' },
    ],
  },
  {
    title: '그 밖',
    items: [
      // 자료실 안에서만 닿는 화면이라 사이드바 골격에 없다(DOC-56 §1.4 · /docs/audit).
      { menuKey: 'settings_security', label: '자료실 감사 기록', hint: '자료 조회 안의 화면 · /docs/audit' },
    ],
  },
  {
    // 안전 관리는 2026-10-27 에 사이드바를 떠나 안전 홈페이지(`safe/` 앱)가 됐다 — 닫을 자리가 여기밖에 없다.
    // 진짜 권한 키라 닫으면 안전 홈페이지의 화면과 안전 API 가 함께 닫힌다.
    title: '안전 홈페이지',
    items: [
      {
        menuKey: 'safety',
        label: '안전 관리',
        hint: 'safe.bomienc.com 전체(대시보드 · 일지 셋) + 안전 API',
        // 사이드바 줄 넷이 들고 있던 별칭(사용자 지시 2026-10-23 「권한진단에서도 … 검색하면 나오게 해」).
        searchAliases: ['안전 홈페이지', '대시보드', '안전현황', 'TBM', '온열', '법정점검', '합동점검', '안전교육일지', '안전교육', '특별교육', '안전보건일지', '위험요인', '순회점검일지', '순회점검', '지적사항', '시정조치'],
      },
    ],
  },
];

/**
 * 「탭만 감춤」인 키 — 서버 권한 표({@code MenuActions})에 없어서 <b>화면만</b> 닫는다.
 *
 * <p>개요·조직도·연락처·건축허가·준공·현장사진은 전부 {@code site_pmis} 한 키로 열린다. 그 키를 닫으면
 * 여섯이 함께 닫히고, 현장 소속이 자기 현장을 굴리는 일까지 함께 멈춘다
 * (백엔드 {@code DepartmentBaseline.SITE_MANAGED}). 그래서 탭마다 이름을 따로 붙였다.
 *
 * <p><b>공정관리는 2026-09-04 에 여기서 빠졌다</b>(V237). 요청표가 그 탭을 개요보다 좁게 못박아
 * 진짜 권한 키가 됐다 — 이제 서버 권한 표에 있고({@code MenuActions}) 공정 트리·곡선 API 를
 * 이 키가 막는다. 여기 남겨 두면 「화면만 닫히고 API 는 열린 키」로 잘못 다뤄진다.
 */
export const TAB_ONLY_GATE_KEYS = new Set<string>([
  'site_pmis_overview',
  'site_pmis_org',
  'site_pmis_contacts',
  'site_pmis_permit',
  'site_pmis_completion',
  'site_pmis_scenery',
  'site_pmis_daily',
  // 실험실 탭들. 실험실 키 하나에 탭이 여럿이라 탭 전용 이름이다(캐드 어시스턴트는 V424 로 닫힌 채 출발).
  'docs_lab_homepage',
  'docs_lab_design',
  'docs_lab_earthwork',
  'docs_lab_excavator',
  'docs_lab_bohal',
  'docs_lab_hwp',
  'docs_lab_dococr',
  'docs_lab_apprelease',
  'docs_lab_cad',
]);

/**
 * 게이트로만 판정하는 키 — 라우트 가드가 <b>메뉴 권한 검사를 건너뛴다</b>.
 *
 * 이 화면들은 로그인한 전원에게 열려 있던 곳이라 `visibleMenuKeys` 에 담겨 오지 않는다 —
 * 다른 메뉴와 같은 줄에서 판정하면 게이트를 열어도 <b>아무도</b> 못 들어간다. 게이트만 보고
 * 통과시킨다(`lib/auth/access.ts`).
 *
 * ★ `office_dashboard` 는 여기 들지 않는다. 그쪽은 백엔드가 아는 진짜 메뉴 키라 권한 검사를
 *   그대로 받아야 한다 — 건너뛰면 권한 없는 사람에게 화면이 열리고 표만 비어 보인다.
 */
export const GATE_ONLY_MENU_KEYS = new Set<string>(['ai_agent']);

/**
 * 인사자료 세 문의 메뉴 키 — 백엔드 {@code HrDataAccess} 의 {@code MENU_RECORDS}·{@code MENU_PAYROLL}·
 * {@code MENU_EVALUATION} 과 <b>같은 값</b>이어야 한다.
 *
 * <p>이 셋은 <b>계정 종류가 아니라 역할</b>로 열린다. 그래서 「권한이 없습니다」 한 줄로 끝내면
 * 사용자가 다음에 무엇을 해야 하는지 알 수 없다 — 홈의 안내가 이 집합으로 그 경우를 알아보고
 * 「어느 역할을 받아야 열리는지」까지 적는다({@code HomePage}).
 *
 * <p><b>사이드바에서 지우는 데는 쓰지 않는다.</b> 어제까지 쓰던 메뉴가 예고 없이 사라지면 사유를
 * 말할 자리조차 없어진다(`useMenuTree` 머리말). 막는 자리는 라우트 가드 하나이고, 값 자체를 가리는
 * 것은 서버가 한다.
 */
export const HR_DATA_MENU_KEYS = new Set<string>([
  'hr_records',
  /*
    ★ **인사자료 관리의 다섯 화면은 여기 넣지 않는다** (2026-08-28).

    이 집합은 백엔드 `HrDataAccess` 의 세 상수와 <b>같은 값</b>이어야 한다(위 머리말). 사이드바에
    줄이 늘었다고 여기에 더하면, 홈의 안내가 「이 역할을 받아야 열립니다」 를 <b>있지도 않은 키</b>에
    대해 말하게 된다. 그 다섯은 애초에 권한 키가 아니다 — `hr_records` 로 열리는 사이드바 줄이다.
  */
  'hr_records_payroll',
  'hr_records_evaluation',
]);

/**
 * 사이드바 <b>최상위 메뉴의 기본 순서</b>. {@link DEFAULT_MENU_CONFIG} 의 최상위 `sortOrder` 가
 * 이 배열과 같아야 한다(테스트가 지킨다).
 *
 * <p><b>덤프는 이 순서를 흔들지 못한다.</b> v1 에서 데이터를 덤프해 오면 `menu_settings` 에 v1 시절의
 * `sort_order` 가 통째로 실려 오는데, 그 행들은 `origin='v1_import'` 로 표시돼 있어
 * {@link mergeMenuConfig} 가 <b>라벨만</b> 쓰고 순서는 여기를 따른다.
 *
 * <p>반면 <b>관리자가 메뉴 관리 화면에서 바꾼 순서(`origin='admin'`)는 이 배열을 이긴다.</b>
 * 순서를 사람이 정할 수 있어야 한다는 요구와, 이관 작업의 부산물로 순서가 바뀌면 안 된다는 요구를
 * 함께 만족시키는 자리가 출처(origin)다.
 */
export const ROOT_MENU_ORDER = [
  // 1. 공지사항 (2026-09-18 · 옛 「보미소식」 그룹 자리). 그룹 머리가 아니라 <b>화면으로 가는 줄</b>이라
  //    키가 `news`(권한 없는 그룹 키)가 아니라 `news_notices` 다 — 그룹 키를 링크로 쓰면 아무도
  //    갖고 있지 않은 키로 판정돼 전원에게 안 보인다.
  'news_notices',
  // 2. 게시판 — <b>공지사항 바로 아래</b>다(사용자 지시 2026-09-03). 회사가 내리는 소식 다음에
  //    직원이 직접 쓰는 자리가 온다. 요청게시판 옆이 아닌 이유: 그쪽은 시스템에 무엇을 고쳐 달라는
  //    창구이고, 이쪽은 사람에게 하는 말이다.
  'board',
  'site_status',    // 3. 현장 현황
  'work',           // 4. 업무 일정
  // 전자결재가 여기(5)였다. 2026-09-22 에 사이드바 줄을 걷고 헤더 버튼 하나로 통일하면서
  // 뒤의 다섯이 한 칸씩 당겨졌다 — 이 목록과 골격의 sortOrder 는 <b>구멍 없이</b> 이어져야 한다.
  'docs',           // 5. 자료실
  // 6. 보미기술집 — <b>최상위로 돌아왔다</b>(사용자 지시 2026-09-22). 2026-09-18 에 게시판 안으로
  //    들어갔다가 나흘 만에 제자리다. 들어간 뒤 게시판 하위가 여섯 줄이 되면서, 한 번 펴고 또 한 번
  //    펴야 기술집 글에 닿았다 — 자료실 바로 다음, 옛 자리 그대로 선다.
  'tech',           // 6. 보미기술집
  'assets',         // 7. 자산관리
  // 8. 총무 관리 (사용자 지시 2026-10-19 · DOC-120). 자산관리 바로 뒤 — 자산관리에서 옮겨 온 줄들이라
  //    옛 자리를 찾던 손이 한 칸만 내려가면 닿는다. 인사총무팀·공사전략팀만 보는 그룹이다(V497).
  'general_affairs',
  // 안전 관리가 여기(9)였다. 2026-10-27 에 안전 홈페이지(`safe/` 앱)로 떠나면서 뒤의 둘이 한 칸씩 당겨졌다.
  // 9. 원격지원 (사용자 지시 2026-10-27 · DOC-137 — 「인사관리 총무관리처럼 메뉴로」). 총무 관리 바로 뒤.
  'remote_support',
  'hr',             // 10. 인사관리
  'settings',       // 11. 환경설정
] as const;

/*
  ★ CS(입주민 커뮤니티)는 <b>최상위가 아니다</b> (사용자 지시 2026-09-29:
    「cs 메뉴를 만들라고 한 적이 없는데, 현장현황에 준공 칸을 만들어서 거기로 옮기자」).

  한때 여기 아홉째로 섰다가 같은 날 걷었다. 입주민의 하자도 게시판도 <b>그 현장의 일</b>이고,
  현장은 입찰 → 진행 → 준공으로 흐른다 — CS 는 그 흐름의 마지막 칸이지 별개의 메뉴가 아니다.
  지금 그 자리는 `site_status_completed`(현장 현황 › 준공) 하나다.
*/

export const DEFAULT_MENU_CONFIG: MenuConfigItem[] = [
  // 관리자 대시보드(super_admin 전용)는 사이드바 메뉴가 아니라 상단 헤더에 있다(Navigation) — 여기에 두지 않는다.
  // 현장 현황(목록·카드 보드). 상세(현장 조회, /sites/pmis)는 메뉴에 없고 카드 클릭으로만 진입한다(DOC-12).
  //
  // 이름은 <b>현장 현황</b>이다(사용자 지시 2026-09-18). 이 화면은 이름이 세 번 바뀌었다 —
  // 현장 정보(DOC-12) → 현장 현황판(DOC-49) → 현장 정보 → <b>현장 현황</b>. 그래서 옛 이름 셋을
  // 전부 검색 별칭으로 남긴다: 즐겨찾기와 머릿속에 남은 이름으로도 찾아갈 수 있어야 한다.
  //
  // 이름을 정하는 자리가 셋이라 <b>셋을 함께</b> 바꾼다: 여기 라벨 · `SiteStatusPage` 폴백 ·
  // 운영 DB(`sys_menu_settings`). `useMenuLabel` 이 DB 를 먼저 보므로 DB 쪽은 마이그레이션
  // (`V301`)이 맡고, v1 sync 가 다시 덮지 않도록 `V2_RENAMED_MENU_LABEL_KEYS` 에도 넣었다.
  { menuKey: 'site_status', label: '현장 현황', groupKey: null, sortOrder: 3, itemType: 'link', href: '/sites/status', searchAliases: ['현장 현황판', '현장 정보', '현황판', '현장정보판'] },
  /*
    하위 둘 — <b>진행 · 입찰</b> (사용자 지시 2026-09-18).

    부모를 누르면 진행이 뜬다. `work_weekly` 와 같은 모양이다: 부모가 `link` 이면서 하위를 갖고,
    부모를 눌러도 하위를 눌러도 갈 곳이 있다.

    ★ 진행은 <b>새 권한 키를 만들지 않는다</b>(`visibleWhenAnyOf: ['site_status']`). 지금 현장
      현황을 보는 전원이 그대로 본다 — 키를 새로 만들면 배포되는 날 전원에게서 화면이 사라지고,
      관리자가 100명에게 손으로 다시 붙여야 한다.
    ★ 입찰만 자기 키다(`site_status_bid` · V303). 아직 수주하지 못한 금액이 실리고 등록하는 손이
      영업이라, 누가 보고 누가 넣는지를 따로 정할 수 있어야 한다. <b>게이트로 닫아서 낸다.</b>
  */
  { menuKey: 'site_status_active', label: '진행', groupKey: 'site_status', sortOrder: 1, itemType: 'link', href: '/sites/status/active', visibleWhenAnyOf: ['site_status'], searchAliases: ['진행현장', '현장 목록'] },
  { menuKey: 'site_status_bid', label: '입찰', groupKey: 'site_status', sortOrder: 2, itemType: 'link', href: '/sites/status/bids', searchAliases: ['입찰', '수주', '공고', '현설', '현장설명회', '투찰', '낙찰', '계약검토', '착공준비'] },
  /*
    입찰 아래 <b>갈래 둘 — 입찰 · 견적</b> (사용자 확정 2026-09-30 「입찰 / 견적 으로 일단 가보자」).

    한 낱말이 <b>방향이 반대인 두 일</b>을 가리키고 있었다.
    <ul>
      <li><b>입찰</b> — 발주처(LH·지자체)의 공고를 보고 <b>우리가 투찰</b>한다. 돈이 들어온다.
      <li><b>견적</b> — 우리가 협력사에게 <b>물어서 받는다</b>. 돈이 나간다. 나이스다큐가 하는 일이
          이쪽이고, 그 자리가 현장 조회 › 견적·입찰 탭에만 있어서 현장을 고른 뒤에야 보였다 —
          그것이 「우리 회사 사람들이 쓰기엔 너무 불편하다」의 정체다.
    </ul>

    <p>「수주 / 발주」로 한 번 적었다가 사용자 지적으로 걷었다 — 회계 쪽 말투라 물어봐야 알았다.
    나란히 서는 두 낱말이 <b>각자 무슨 일인지</b>를 말해야 하고, 방향은 그 아래 화면이 말한다.

    <p>두 줄은 머리줄 탭의 <b>둘째 줄</b>로 선다({@code useMenuSection} — 고른 첫 줄 항목의 자식).
    사이드바에는 「현장 현황」 한 줄뿐이고 진행·입찰·준공이 첫 줄, 이 둘이 그 아래다. 부모와 첫
    자식이 같은 이름인 것은 그대로 둔다 — 부모를 누르면 그 첫 자식으로 가므로 어긋나지 않는다.

    ★ 입찰(수주 쪽)은 <b>새 권한 키를 만들지 않는다</b>(`visibleWhenAnyOf: ['site_status_bid']`).
      부모와 <b>같은 화면·같은 주소</b>라 키를 새로 내면 입찰을 보던 사람에게서 화면이 사라진다.
    ★ 견적은 <b>기존 키를 그대로 쓴다</b>(`site_pmis_rfq` · V359). 현장 조회 탭이던 동안의 게이트
      행·역할 그랜트(외주구매팀·공사전략팀·공무)·`MenuActions` 가 그 이름에 붙어 있다 — 새 키를
      만들면 셋을 다시 붙이는 마이그레이션이 필요하고, 그날 하루는 아무에게도 안 열린다.
      게이트에 행이 있으므로 이 줄은 <b>닫힌 채로</b> 난다(관리자만 본다).
  */
  /*
    ★ 자식의 주소가 부모와 <b>다르다</b>({@code /bids/tender}). 같은 화면인데 주소를 나누는 것은
      「현장 현황 → 진행」이 이미 한 일이다(`menuRoutes.test` 가 막는다) — 한 주소를 두 메뉴가
      가리키면 사이드바·탭의 활성 표시가 둘 다 켜지고, 어느 칸에 서 있는지가 화면에서 사라진다.
      부모 주소({@code /bids})로 들어와도 이 칸이 켜진다({@code useMenuSection} 의 `landedOnTab`).
  */
  { menuKey: 'site_status_bid_tender', label: '입찰', groupKey: 'site_status_bid', sortOrder: 1, itemType: 'link', href: '/sites/status/bids/tender', visibleWhenAnyOf: ['site_status_bid'], searchAliases: ['입찰', '수주', '투찰', '낙찰', '계약검토', '착공준비', '발주처', '나라장터'] },
  { menuKey: 'site_pmis_rfq', label: '견적', groupKey: 'site_status_bid', sortOrder: 2, itemType: 'link', href: '/sites/status/rfqs', searchAliases: ['견적', '발주', '견적요청', '입찰공고', '개찰', '응찰', '초청', '협력사 견적', 'RFQ', '나이스다큐'] },
  /*
    준공 — <b>CS 가 사는 자리</b> (사용자 지시 2026-09-29).

    입주민 앱(보미건설 입주자센터)의 반대편이 전부 여기 있다: 하자 접수함 · 그 현장 게시판의
    글 관리 · 입주민 승인 · 현장 설정(세대·담보기간·시설·매뉴얼).

    ★ 최상위 「CS」 메뉴를 세웠다가 같은 날 걷고 이 칸으로 옮겼다. 하자도 게시판도 <b>그 현장의
      일</b>이고, 현장은 입찰 → 진행 → 준공으로 흐른다 — 준공한 현장에서 하는 일이 CS 다.
  */
  { menuKey: 'site_status_completed', label: '준공', groupKey: 'site_status', sortOrder: 3, itemType: 'link', href: '/sites/status/completed', searchAliases: ['준공', '하자', 'AS', '입주민', 'CS', '커뮤니티', '하자접수', '세대', '시설예약', '매뉴얼'] },

  // 업무 일정 — 월간회의 3화면 · 주간회의 3화면 · 통합 캘린더를 두 화면의 상단 탭으로 합친 것이다.
  // 사이드바에 달력이 여럿 걸려 있어 "이번 주에 뭐가 있나" 를 알려면 화면을 차례로 열어야 했다.
  // 대시보드는 달력이 아니라 한 주를 요약하는 화면이라 탭에 넣지 않고 형제로 둔다.
  //
  // v1 의 일곱 화면은 <b>하위 항목으로 되돌아왔다</b>(DOC-24). 합치면서 주소도 화면도 권한도 그대로 두었지만,
  // 사람은 URL 이 아니라 왼쪽 목록의 위치로 길을 외운다 — 즐겨찾기를 살린 것으로는 매일 클릭하던 손을 살리지 못했다.
  // 하위 항목과 상단 탭은 <b>같은 주소</b>를 가리킨다. 입구가 둘이고 목적지가 하나라 서로 어긋날 수 없다.
  { menuKey: 'work', label: '업무 일정', groupKey: null, sortOrder: 4, itemType: 'group' },
  {
    menuKey: 'work_weekly',
    label: '주간업무',
    groupKey: 'work',
    sortOrder: 2,
    itemType: 'link',
    href: '/meetings/weekly',
    visibleWhenAnyOf: ['dashboard', 'meetings_hq', 'meetings_site', 'meetings_completed'],
    searchAliases: ['주간회의'],
  },
  // 주간업무의 하위 = 대시보드 + v1 「주간회의」의 세 화면. 통합 화면(`/meetings/weekly`)은 볼 수 있는 첫 탭으로 물러서므로
  // 부모를 눌러도, 하위를 눌러도 갈 곳이 있다.
  // 대시보드는 업무 일정의 형제였다가 주간업무 첫 칸으로 들어왔다(사용자 지시 2026-10-21). 주소(`/dashboard`)는 그대로다.
  { menuKey: 'dashboard', label: '대시보드', groupKey: 'work_weekly', sortOrder: 0, itemType: 'link', href: '/dashboard' },
  { menuKey: 'meetings_hq', label: '본사', groupKey: 'work_weekly', sortOrder: 1, itemType: 'link', href: '/meetings/hq', searchAliases: ['주간회의 본사'] },
  { menuKey: 'meetings_site', label: '현장', groupKey: 'work_weekly', sortOrder: 2, itemType: 'link', href: '/meetings/site', searchAliases: ['주간회의 현장'] },
  { menuKey: 'meetings_completed', label: '준공현장', groupKey: 'work_weekly', sortOrder: 3, itemType: 'link', href: '/meetings/completed', searchAliases: ['주간회의 준공현장'] },
  {
    menuKey: 'work_monthly',
    label: '월간업무',
    groupKey: 'work',
    sortOrder: 3,
    itemType: 'link',
    href: '/meetings/monthly',
    visibleWhenAnyOf: ['monthly_hq', 'monthly_field', 'monthly_site', 'calendar'],
    searchAliases: ['월간회의', '통합 캘린더'],
  },
  // 월간업무의 하위 = '전체'(통합 달력) + v1 「월간회의」의 세 화면.
  //
  // '전체'는 한동안 하위로 두지 않았다(부모와 주소가 같아서). 그러자 `/meetings/monthly` 로 들어오면
  // 화면은 전체인데 둘째 줄 탭에는 전체가 없고 첫 칸(본사)이 켜졌다 — 탭 줄이 「지금 어디」를 거짓으로
  // 말했다(사용자 지적 2026-10-01). 그래서 자기 주소(`/meetings/monthly/all`)를 주고 첫 칸에 세운다.
  // 부모 주소로 들어오면 첫 자식이 켜지는 규칙(`useMenuSection` 의 `landedOnTab`)이 이 칸을 받는다.
  // 권한 키는 그대로 `calendar` 다 — 연차 조회 API 와 '전체' 탭이 이미 그 키로 갈린다.
  { menuKey: 'calendar', label: '전체', groupKey: 'work_monthly', sortOrder: 0, itemType: 'link', href: '/meetings/monthly/all', searchAliases: ['통합 캘린더', '월간업무 전체'] },
  { menuKey: 'monthly_hq', label: '본사', groupKey: 'work_monthly', sortOrder: 1, itemType: 'link', href: '/meetings/monthly/hq', searchAliases: ['월간회의 본사'] },
  { menuKey: 'monthly_field', label: '현장 주요일정', groupKey: 'work_monthly', sortOrder: 2, itemType: 'link', href: '/meetings/monthly/field', searchAliases: ['월간회의 현장 주요일정'] },
  { menuKey: 'monthly_site', label: '현장 회의일정', groupKey: 'work_monthly', sortOrder: 3, itemType: 'link', href: '/meetings/monthly/site', searchAliases: ['월간회의 현장 회의일정'] },

  // 총무 관리가 8 로 들어오면서(2026-10-19) 9 였고, 안전 관리가 9 로 들어오면서(2026-10-20) 10 이다.
  { menuKey: 'hr', label: '인사관리', groupKey: null, sortOrder: 10, itemType: 'group' },
  // 보기 셋(명부·전술판·조직도)은 사이드바에 줄을 세우지 않는다 — 한 화면 안의 보기 전환이고,
  // 줄을 세우려면 주소·권한 키·시딩이 각각 따라붙는다(현장 조회 탭을 세우지 않는 것과 같은 판단).
  // 대신 <b>그 이름으로 찾아오는 사람</b>을 여기서 받는다: 검색은 라벨과 이 별칭을 함께 본다.
  {
    menuKey: 'hr_placement',
    label: '인력배치표',
    groupKey: 'hr',
    sortOrder: 1,
    itemType: 'link',
    href: '/hr/placement',
    searchAliases: ['명부', '인원배치', '배치표'],
    // 전술판·조직도는 <b>그 보기가 열려 있을 때만</b> 검색에 걸린다. 닫아 둔 것이 검색으로
    // 나오면 「있는데 못 쓰는 기능」이 되고, 그 사람은 자기 권한을 의심한다.
    gatedSearchAliases: [
      { alias: '전술판', gateKey: 'hr_placement_board' },
      { alias: '조직도', gateKey: 'hr_placement_chart' },
      { alias: '자리배치도', gateKey: 'hr_placement_seat' },
    ],
  },
  /*
    ★ **AI 경력매치 줄은 내려갔다** (사용자 결정 2026-09-23) — 묻는 자리가 <b>AI Agent 대화</b>로
      옮겨 갔다(`agent/app/tools/hr.py` 의 `match_careers`). 「지하 흙막이 해 본 사람」 같은 물음은
      애초에 칸에 적는 말이 아니라 하는 말이라, 화면 하나를 따로 세워 둘 일이 아니었다.

    ★ <b>키(`hr_search`)는 살아 있다.</b> 도구가 부르는 문이 그 키로 막히고
      ({@code CareerMatchController.MENU}), 화면도 라우트로는 남아 있다(`screens.tsx`) — 모바일 앱이
      그 주소를 계속 쓴다. 키를 지우면 <b>그랜트와 도구가 함께 죽는다.</b>

    ★ <b>오픈 게이트(`sys_menu_gate`)로 닫으면 안 된다.</b> 그 게이트는 사이드바가 아니라 서버
      인가라({@code UserMenuPermissionResolver}) 닫는 순간 관리자 외에는 AI 도구까지 403 이 된다.
      내리는 자리는 여기, 즉 <b>골격의 줄</b> 하나다.

    권한을 주는 자리는 {@code permissionTree.ATTACHED_KEYS} 로 옮겼다 — 사이드바 줄이 없어지면
    매트릭스에서 사라져 아무도 그 권한을 줄 수 없게 되기 때문이다.
  */
  /*
    ★ **경력관리와 인사자료 관리를 한 줄로 합쳤다** (사용자 지시 2026-09-23).

    두 화면이 하던 일은 <b>같은 사람을 여는 것</b>이었다. 왼쪽에 같은 명부를 두 번 그리고, 한쪽에서
    사람을 고른 뒤 「인사자료 관리」 버튼으로 건너가면 저쪽 명부에서 같은 사람을 다시 찾아야 했다 —
    실제로 그 왕복을 줄이려고 `?personnelId=` 를 실어 보내는 코드가 양쪽에 있었다. 명부가 하나면
    그 코드도, 그 왕복도 필요 없다.

    ★ **권한 키는 둘 다 산다** (사용자 결정). 줄은 하나지만 <b>탭마다 원래 키로 판정</b>한다 —
      「경력」 탭은 {@code hr_management}, 연차·부재·인사고과·자격증·지급 자산은 {@code hr_records}
      ({@code HrRecordsPage} 의 `useMenuCapability`). 합쳤다고 키를 새로 만들면 이미 부여해 둔
      권한이 전부 무효가 되고, 무엇보다 <b>경력관리만 받던 사람에게 급여·고과가 열린다</b>.

    ★ 그래서 입구는 {@code visibleWhenAnyOf} 로 연다 — 둘 중 하나라도 있으면 줄이 선다.
      아래 하위 일곱 줄은 그대로 {@code hr_records} 하나로 열리므로(그 판단은 바로 다음 주석),
      경력관리만 받은 사람에게는 <b>이 줄 하나와 「직원 자료」만</b> 보인다.

    ★ 이름을 「인사자료」로 줄였다. 「인사자료 관리」·「경력관리」 두 이름을 아는 사람이 각각
      있으므로 <b>둘 다 별칭</b>으로 남긴다 — 옛 이름을 치는 사람이 곧 길을 잃은 사람이다.
      라벨은 서버 설정(`sys_menu_settings`)이 이기므로, 운영 DB 에 이 키의 행이 있으면 거기서도
      한 번 바꿔야 화면에 반영된다({@code mergeMenuConfig}).
  */
  {
    menuKey: 'hr_records',
    label: '인사자료',
    groupKey: 'hr',
    sortOrder: 3,
    itemType: 'link',
    href: '/hr/records',
    visibleWhenAnyOf: ['hr_management'],
    searchAliases: ['인사자료 관리', '경력관리', '인력관리', '경력', '기술등급', '역량지수', '진급', '배치'],
  },
  /*
    ★ **다섯 화면을 하위 메뉴로 낸다** (사용자 지시 2026-08-28: 「하위 메뉴로 직원자료
      연차촉진 공휴일 복지 수당기준 숙소귀향 이걸 메뉴로 넣어버리자」).

    탭만 있을 때는 그 화면들이 <b>사이드바에서 보이지 않았다</b> — 「숙소·귀향」 을 찾는
    사람이 인사자료 관리 안에 있다는 것을 알 길이 없고, 그 화면을 주소로 보낼 수도 없다.

    ★ **권한을 새로 만들지 않는다.** 다섯 줄 모두 `hr_records` 로 열린다(`visibleWhenAnyOf`) —
      메뉴를 나눠놓고 키까지 나누면 이미 부여해 둔 권한이 전부 무효가 되고 관리자가
      사람마다 다시 켜야 한다(주간·월간업무가 같은 이유로 그렇게 되어 있다).
      서버의 문도 그대로 `hr_records` 하나다.
  */
  /*
    ★ 「직원 자료」만 {@code hr_management} 도 연다 (2026-09-23 · 위 합치기). 이 줄이 곧 <b>합쳐진
      화면</b>이고, 부모 줄도 여기로 온다. 나머지 여섯은 사람 하나를 파는 화면이 아니라 전 직원을
      훑는 인사 업무라 {@code hr_records} 하나로 남는다 — 경력관리만 받은 사람에게 연차 촉진·
      성과급이 열리면 그것이 곧 합치기의 실패다.
  */
  /*
    대시보드(사용자 지시 2026-11-02) — 재직·입퇴사·이직률 집계와 재직자/입사자/퇴사자 현황·사원별 경력카드 탭.
    권한 키는 따로 없다(`hr_records`). 게이트(V611)로 닫힌 채 나간다.
  */
  { menuKey: 'hr_records_dashboard', label: '대시보드', groupKey: 'hr_records', sortOrder: 0, itemType: 'link', href: '/hr/records/dashboard', visibleWhenAnyOf: ['hr_records'], searchAliases: ['인사 대시보드', '재직자 현황', '입사자 현황', '퇴사자 현황', '경력카드', '이직률', '재직인원'] },
  { menuKey: 'hr_records_staff', label: '인사자료', groupKey: 'hr_records', sortOrder: 1, itemType: 'link', href: '/hr/records/staff', visibleWhenAnyOf: ['hr_records', 'hr_management'], searchAliases: ['연차', '자격증', '부재', '경력', '기술등급', '개인정보'] },
  /*
    ★ **머리 줄의 「건너가기」 버튼 셋이 사이드바로 올라왔다** (사용자 지시 2026-09-23:
      「다른 탭으로 가는 거면 위에 직원자료·연차촉진 이런 곳으로 올려, 여따 두지 말고」).

    기술인 관리 · 승진 관리 · 인사 기준표가 그 셋이다. 셋 다 <b>다른 화면</b>인데 문이 직원 자료
    화면의 머리 줄에만 있었다 — 그 화면을 열지 않는 사람은 그런 화면이 있다는 것조차 몰랐고,
    머리 줄은 엑셀까지 합쳐 여덟 칸이 되어 정작 「인력 등록」이 묻혔다.

    ★ **권한 키를 새로 만들지 않는다** — 위 여섯 줄과 같은 규칙이다. 노출은 {@code
      visibleWhenAnyOf} 로만 가르고, 그 값은 <b>라우트 가드가 보는 키와 같은 글자</b>여야 한다
      ({@code screens.tsx}): 기술인 관리는 {@code hr_management}, 나머지 둘은 {@code hr_records}.
      어긋나면 「사이드바에는 보이는데 눌러서 못 들어가는 줄」이 생기고, 그 사람은 자기 권한을
      의심한다.

    ★ **옛 판단이 뒤집혔다.** 승진 관리·인사 기준표에는 「정기적으로 한 번씩 하는 일이라 늘
      사이드바를 차지할 화면이 아니다」(2026-09-03)라는 판단이 적혀 있었다. 2026-09-23 에
      사용자가 그것을 뒤집었다 — 가끔 하는 일일수록 <b>어디에 있었는지 기억나지 않는다</b>.
      되돌리려는 사람은 이 두 문단을 함께 읽고 정하면 된다.

    순서는 <b>일의 흐름</b>이다: 사람을 여는 줄(1) → 전 직원을 훑는 일(2~4) → 그 일이 쓰는
    기준을 넣는 자리(5~7) → 올라온 요청을 처리하는 자리(8~9).
  */
  { menuKey: 'hr_records_technicians', label: '기술인 관리', groupKey: 'hr_records', sortOrder: 2, itemType: 'link', href: '/hr/technicians', visibleWhenAnyOf: ['hr_management'], searchAliases: ['기술인', '기술등급', '역량지수', '협회', '건설기술인', '승급', '계속교육'] },
  /*
    업무분장(V599 · 사용자 지시 2026-10-30) — 회사 업무분장 엑셀 한 장을 옮긴 전사 표. 권한 키는 새로 만들지 않는다
    (`hr_records` · 사용자 결정 2026-10-30). 게이트(V599)로 닫힌 채 나간다.
  */
  { menuKey: 'hr_records_duties', label: '업무분장', groupKey: 'hr_records', sortOrder: 3, itemType: 'link', href: '/hr/records/duties', visibleWhenAnyOf: ['hr_records'], searchAliases: ['업무분장', '담당업무', '업무 분장', 'R&R', '담당자', '업무ID'] },
  { menuKey: 'hr_records_promotions', label: '승진 관리', groupKey: 'hr_records', sortOrder: 4, itemType: 'link', href: '/hr/records/promotions', visibleWhenAnyOf: ['hr_records'], searchAliases: ['승진', '진급', '승진 상신', '정기 승진', '직급'] },
  // 공휴일은 근태 관리 › 공휴일 탭으로 옮겼다(사용자 지시 2026-11-02).
  /*
    인사고과(사용자 지시 2026-11-02: 「넣어야지」) — 화면(`/hr/records/evaluation`)은 있었는데 사이드바 줄이 없어 기능 검색으로만 닿았다.
    서버는 고과 담당 키(`hr_records_evaluation`)로 막으므로 줄도 그 키를 가진 사람에게만 선다 — 인사팀이라고 열면
    눌렀을 때 빈 표만 본다. 줄 키는 권한 키와 겹치지 않게 `hr_records_evaluations`.
    ★ 게이트로 닫지 않았다 — 새 기능이 아니라 이미 열려 있던 화면에 길을 낸 것이다.
  */
  { menuKey: 'hr_records_evaluations', label: '인사고과', groupKey: 'hr_records', sortOrder: 5, itemType: 'link', href: '/hr/records/evaluation', visibleWhenAnyOf: ['hr_records_evaluation'], searchAliases: ['고과', '연도별 고과', '평가', '인사평가'] },
  { menuKey: 'hr_records_standards', label: '복지·수당 기준', groupKey: 'hr_records', sortOrder: 6, itemType: 'link', href: '/hr/records/standards', visibleWhenAnyOf: ['hr_records'], searchAliases: ['건강검진', '경조금', '장학금', '법정의무교육', '수당', '법정 선임'] },
  /*
    인사 기준표(`/hr/standards`) — <b>바로 윗줄과 다른 화면</b>이다. 키를 `hr_records_standards`
    로 지으면 그 줄을 덮어쓰므로 이름을 달리 지었다. 이 줄은 진급 판정이 「기준 미등록」이라고
    말할 때 찾아가는 자리이고, 윗줄은 복지·수당의 기준을 넣는 자리다.
  */
  { menuKey: 'hr_records_criteria', label: '인사 기준표', groupKey: 'hr_records', sortOrder: 7, itemType: 'link', href: '/hr/standards', visibleWhenAnyOf: ['hr_records'], searchAliases: ['기준표', '인사기준', '승진 요건', '진급 기준', '호봉'] },
  { menuKey: 'hr_records_relocation', label: '숙소·귀향', groupKey: 'hr_records', sortOrder: 8, itemType: 'link', href: '/hr/records/relocation', visibleWhenAnyOf: ['hr_records'], searchAliases: ['숙소', '귀향여비', '숙소배정', '사택', '기숙사'] },
  /*
    성과급(V308 · 사용자 지시 2026-09-21). 위 다섯과 <b>같은 부류</b>라 같은 자리에 선다 —
    인사팀이 정기적으로 들어와 처리하는 화면이고, 사이드바에 없으면 주소를 아는 사람만 연다.

    ★ 권한 키는 그대로 `hr_records` 다(`visibleWhenAnyOf`). 키를 새로 만들면 이미 인사자료
      관리를 쓰는 사람 전원에게서 이 화면이 사라지고 관리자가 손으로 다시 켜야 한다.
  */
  { menuKey: 'hr_records_incentive', label: '성과급', groupKey: 'hr_records', sortOrder: 9, itemType: 'link', href: '/hr/records/incentive', visibleWhenAnyOf: ['hr_records'], searchAliases: ['성과급', '기여도', '지급금 한도', '평가등급', '인센티브'] },
  // 휴무 관리(2026-08-24 · 2026-08-26 계획서 탭). 인사자료 관리 바로 옆이다 — 원장을 고치는 화면과
  // 그 원장을 한 판으로 읽는 화면이라, 표에서 이상한 값을 본 사람이 곧바로 옆 줄로 간다.
  // 옛 이름(연차 현황)으로 찾는 사람이 한동안 남으므로 별칭에 남겨 둔다.
  /*
    ★ **누르면 펼쳐지기만 한다** (사용자 지시 2026-09-01: "계획서 메뉴를 눌러야지만 이동이 되어야해").

    예전에는 `link` 라 부모 줄이 곧 이동이었고, 그 이동이 `/hr/leave` → `/hr/leave/plan` 을 거쳐
    <b>바깥 대시보드</b>까지 갔다 — 무엇이 있는지 보려고 누른 사람이 새 탭에 던져졌다.
    이제 `group` 이라 <b>갈 곳이 없고</b>, 나가는 것은 아래 「계획서」 한 줄뿐이다.

    ★ 권한 키(`hr_leave_status`)는 그대로 살아 있다 — 서버의 판정도, 옛 이름으로 찾는 별칭도
      이 줄에 붙어 있다. 다만 그룹은 <b>자식이 하나도 안 보이면 함께 사라진다</b>(`useMenuTree`).
      그래서 지금은 계획서가 보이는 사람(임원·본부장·팀장·총괄소장·현장대리인·인사자료 담당)에게만
      이 줄이 선다 — 「눌러도 아무것도 없는 줄」 이 남지 않는다.

    ★ 되돌리는 법: 아래 주석 줄을 살리면 예전처럼 부모가 화면으로 간다.
  */
  { menuKey: 'hr_leave_status', label: '휴무 관리', groupKey: 'hr', sortOrder: 5, itemType: 'group', searchAliases: ['연차', '휴가', '연차 사용', '연차 현황', '연차 현황표', '휴무 계획서', '사용계획서', '휴무계획'] },
  /*
    근태 관리(옛 출결 관리 · 사용자 지시 2026-09-03). 찍는 것은 앱이고 <b>보는 것은 웹뿐</b>이다 — 남의 근태를
    훑고 잘못 찍힌 줄을 바로잡는 일은 앉아서 표를 보며 하는 업무다.
    2026-10-26 이름을 바꾸고 인사자료의 연차관리 표를 탭으로 들였다 — 옛 이름과 연차관리의 별칭을 함께 든다.
  */
  { menuKey: 'hr_attendance', label: '근태 관리', groupKey: 'hr', sortOrder: 6, itemType: 'link', href: '/hr/attendance', searchAliases: ['출퇴근', '근태', '출근', '퇴근', '출결', '출결 관리', '출결관리', '연차관리', '연차 촉진', '연차사용촉진', '촉진', '연차사용내역', '연차 동의서'] },
  /*
    PC Off (사용자 지시 2026-09-04 · DOC-77). 정해 둔 시각에 사내 PC 를 끈다.

    출결 옆에 두는 이유는 <b>같은 사람이 쓰는 값</b>이기 때문이다 — 몇 시에 나가는가와 몇 시에
    PC 가 꺼지는가는 인사총무팀이 함께 정한다. 부서 소속 기본 조회에서는 빠져 있어
    (`DepartmentBaseline.EXCLUDED`) 권한을 받은 사람에게만 줄이 선다.
  */
  { menuKey: 'hr_pc_off', label: 'PC Off', groupKey: 'hr', sortOrder: 9, itemType: 'link', href: '/hr/pc-off', searchAliases: ['피씨오프', 'PC오프', '자동 종료', '컴퓨터 끄기', '정시퇴근', '셧다운'] },
  // { menuKey: 'hr_leave_status', label: '휴무 관리', groupKey: 'hr', sortOrder: 5, itemType: 'link', href: '/hr/leave', searchAliases: ['연차', '휴가', '연차 사용', '연차 현황', '연차 현황표', '휴무 계획서', '사용계획서', '휴무계획'] },
  // 세 화면도 같은 이유로 사이드바에 낸다(2026-08-28). 권한은 `hr_leave_status` 하나다.
  { menuKey: 'hr_leave_dashboard', label: '대시보드', groupKey: 'hr_leave_status', sortOrder: 1, itemType: 'link', href: '/hr/leave/dashboard', visibleWhenAnyOf: ['hr_leave_status'], searchAliases: ['휴무 달력', '누가 없나'] },
  /*
    ★ **계획서는 잠시 바깥 주소로 보낸다** (사용자 지시 2026-08-31).

    화면 자체를 지우지 않는다 — 코드(`LeavePlanPanel`)도 라우트도 그대로 있고, 여기와
    `screens.tsx` 두 줄만 바깥을 가리킨다. 돌아오는 날 아래 주석을 풀고 이 줄을 지우면 끝이다.

    `external` 이라 <b>새 탭</b>에서 열린다(`Navigation.tsx`) — 사내 화면을 보던 자리를
    빼앗지 않아야, 옆 탭(대시보드·현황)으로 돌아올 때 다시 로그인하지 않는다.

    ★ **`visibleWhenAnyOf` 를 뗐다** (사용자 지시 2026-08-31: 임원·팀장·현장대리인·총괄소장만).
      부모 키를 빌려 쓰면 「휴무 관리가 보이는 사람 = 계획서가 보이는 사람」 이 되어 범위를 좁힐
      자리가 없다. 이제 자기 키로 열리고, 그 키를 여는 것은 <b>자리</b>다
      (`UserMenuPermissionResolver.opensLeaveBoard` — 서버가 `visibleMenuKeys` 에 담아 준다).

      바깥 대시보드는 <b>줄을 잘라 주지 않는다</b> — 한 장이 통째로 보인다. 사내 표(현황)는
      서버가 볼 수 있는 줄만 내려주므로 넓게 열어도 새지 않지만, 이 링크는 그렇지 않다.

    ★ **주소는 여기서 고치지 않는다** (사용자 지시 2026-09-01 · `MENU_EXTERNAL_LINKS`).
      아래 `href` 는 <b>설정이 비었을 때의 기본값</b>이고, 실제로 나가는 주소는 「환경설정 ›
      외부 API 설정」 화면에서 정한다 — 그려질 때 `useMenuTree` 가 갈아 끼운다.
  */
  /*
    ★ 부모의 별칭을 <b>이 줄이 물려받았다</b> (사용자 지시 2026-09-01).
      검색은 그룹 줄을 결과로 내지 않는다(`Navigation` — 갈 곳이 없는 줄이라 그렇다). 부모가
      그룹이 되면서 「연차 현황」 으로 찾던 사람이 빈 결과를 받게 되므로, 그 말들을 지금 유일하게
      열려 있는 목적지인 이 줄로 옮긴다.
  */
  { menuKey: 'hr_leave_plan', label: '계획서', groupKey: 'hr_leave_status', sortOrder: 2, itemType: 'external', href: LEAVE_PLAN_EXTERNAL_URL, searchAliases: ['휴무 계획서', '사용계획서', '휴무 관리', '휴무계획', '연차', '휴가', '연차 사용', '연차 현황'] },
  // { menuKey: 'hr_leave_plan', label: '계획서', groupKey: 'hr_leave_status', sortOrder: 2, itemType: 'link', href: '/hr/leave/plan', visibleWhenAnyOf: ['hr_leave_status'], searchAliases: ['휴무 계획서', '사용계획서'] },
  { menuKey: 'hr_leave_board', label: '현황', groupKey: 'hr_leave_status', sortOrder: 3, itemType: 'link', href: '/hr/leave/board', visibleWhenAnyOf: ['hr_leave_status'], searchAliases: ['연차 현황표', '잔여연차'] },
  // 증명서 서식·직인 관리. 마이페이지의 '증명서 발급' 이 쓰는 서식을 여기서 만들고 고친다(DOC-18 §10.4).
  { menuKey: 'hr_doc_templates', label: '증명서 서식 관리', groupKey: 'hr', sortOrder: 6, itemType: 'link', href: '/hr/doc-templates', searchAliases: ['증명서 양식', '직인', '도장'] },
  { menuKey: 'hr_business_cards', label: '명함 취합', groupKey: 'hr', sortOrder: 7, itemType: 'link', href: '/hr/business-cards', searchAliases: ['명함', '명함신청', '명함발주'] },
  // 기술인 법정교육의 결재 뒷일 — 환급 큐와 협회 대조 도장(DOC-61 · V117). 경력관리(hr_management)와
  // 문을 나눈 이유: 이 화면을 여는 셋 가운데 둘(공사전략팀 팀장·경비 담당)에게 경력관리를 주면
  // 교육과 무관한 인사자료가 함께 열린다.
  { menuKey: 'hr_tech_education', label: '기술인 교육', groupKey: 'hr', sortOrder: 8, itemType: 'link', href: '/hr/tech-education', searchAliases: ['법정교육', '승급교육', '계속교육', '교육비', '환급'] },

  // 공정관리(`process` / `process_write` → scheide.netlify.app)는 내렸다(2026-08-11).
  // 공정표는 현장 조회 공정율 탭 하나로 모은다 — 실험실의 내역서 → 공정율 초안은 엑셀 기반 도입으로
  // 방향이 바뀌어 같은 날 지웠다(DOC-25 §7.5).

  // 자료실 — 문서 분류(대·중·소 관리) + 자료 조회(S3·NAS)로 나뉜다.
  { menuKey: 'docs', label: '자료실', groupKey: null, sortOrder: 5, itemType: 'group' },
  { menuKey: 'docs_categories', label: '문서 분류', groupKey: 'docs', sortOrder: 1, itemType: 'link', href: '/docs/categories' },
  // 자료 조회가 뷰어를 겸한다 — 목록의 [보기] 로 PDF·이미지·도면(dwg·dxf)·3D 를, [문서 편집] 으로
  // 엑셀·워드·PPT 를 연다. 그래서 '도면 뷰어' 항목은 2026-08-18 에 메뉴에서 뺐다(경로 /bim 은
  // 리다이렉트로 살아 있고 권한 키 bim_viewer 도 지우지 않았다 — screens.tsx SCREEN_REDIRECTS).
  // 도면을 찾아 이 메뉴를 뒤지던 사람을 위해 별칭으로 '도면 뷰어' 를 남긴다.
  {
    menuKey: 'docs_browse',
    label: '자료 조회',
    groupKey: 'docs',
    sortOrder: 2,
    itemType: 'link',
    href: '/docs',
    searchAliases: ['도면 뷰어', '도면', 'PDF 뷰어', '엑셀', 'BIM'],
  },
  // 본사가 현장에 자료를 요청하고, 현장이 낸 것을 검토해 자료실로 편입한다(DOC-48).
  // 검토 전 자료는 자료실(docs/) 밖에 있어 자료 조회·AI 검색에 나타나지 않는다 — 승인되어야 편입된다.
  {
    menuKey: 'docs_submissions',
    label: '자료 요청',
    groupKey: 'docs',
    sortOrder: 3,
    itemType: 'link',
    href: '/docs/submissions',
    searchAliases: ['자료 제출', '제출 요청', '제출', '검토', '보완요청', '임시저장'],
  },
  // 실험실 — 업무에 정식으로 넣기 전에 새 기술을 먼저 시험하는 곳(DOC-25 §7.1).
  // 이름이 경계다: 여기 있는 것은 아직 검증 중이며, 현장이 그것을 정본으로 가져다 쓰면 안 된다.
  // 자료실 아래인 이유는 지금 시험하는 것들이 현장 자료(사진·측량·도면)를 재료로 삼기 때문이다.
  {
    menuKey: 'docs_lab', label: '실험실', groupKey: 'docs', sortOrder: 4, itemType: 'link', href: '/docs/lab',
    searchAliases: ['스마트 건설', '작업실'],
    // 탭 이름은 <b>그 탭이 열려 있을 때만</b> 검색에 걸린다(인력배치표의 보기들과 같은 이유).
    gatedSearchAliases: [
      { alias: '토공량', gateKey: 'docs_lab_earthwork' },
      { alias: '절성토', gateKey: 'docs_lab_earthwork' },
      { alias: 'IFC', gateKey: 'docs_lab_earthwork' },
      { alias: '굴착기', gateKey: 'docs_lab_excavator' },
      { alias: '보할', gateKey: 'docs_lab_bohal' },
      { alias: '한글', gateKey: 'docs_lab_hwp' },
      { alias: 'OCR', gateKey: 'docs_lab_dococr' },
      { alias: '성적서', gateKey: 'docs_lab_dococr' },
      { alias: '앱 배포', gateKey: 'docs_lab_apprelease' },
      { alias: '캐드', gateKey: 'docs_lab_cad' },
      { alias: 'CAD', gateKey: 'docs_lab_cad' },
      { alias: '오토캐드', gateKey: 'docs_lab_cad' },
      { alias: 'JTCA', gateKey: 'docs_lab_cad' },
      { alias: '도곽 출력', gateKey: 'docs_lab_cad' },
    ],
  },

  /*
    게시판 — 직원이 쓰고 담당자가 답하는 자리. <b>보미소식 바로 아래</b>다(사용자 지시
    2026-09-03 · ROOT_MENU_ORDER).

    <p><b>개선요청 줄은 없다</b>(사용자 지시 2026-09-22). 2026-09-14 에 여기 들어왔다가 다시 나갔는데,
    나간 곳이 다른 그룹이 아니라 <b>헤더</b>다 — 빨간 「개선사항 요청」 버튼이 이제 창을 열지 않고
    요청게시판 화면으로 간다({@link Navigation}). 사이드바 줄과 헤더 버튼이 같은 곳을 가리키던
    상태에서 줄 쪽을 걷은 것이라, 들어가는 문은 <b>줄지 않고 하나로 모였다</b>.

    ★ 화면(`/requests`)도 라우트도 권한 키({@code requests})도 그대로다. 사이드바에 줄이 없으므로
      권한 매트릭스에서는 게시판 밑에 매단다({@code permissionTree} 의 `ATTACHED_KEYS`).
  */
  { menuKey: 'board', label: '게시판', groupKey: null, sortOrder: 2, itemType: 'group' },
  { menuKey: 'board_thanks', label: '윤리경영제보센터', groupKey: 'board', sortOrder: 1, itemType: 'link', href: '/board/thanks', searchAliases: ['윤리', '윤리경영', '제보', '제보센터', '신고', '익명', '감사합니다'] },
  /*
    옛 「보미소식」의 다섯 줄이 여기로 내려왔다 (사용자 지시 2026-09-18).

    <p>다섯 다 <b>글이 쌓이는 자리</b>다 — 누군가 올리고 여럿이 읽는다. 보미소식에 남을 이유였던
    「회사가 내리는 소식」 은 공지사항 하나로 충분하고, 나머지는 게시판과 성격이 같다.

    ★ 기존 줄(윤리경영제보센터)이 <b>위에 남는다</b>(사용자 지시) — 자리를 외운 손이 이미 있는
      줄이라 새로 온 것을 그 위에 얹지 않는다.
    ★ 메뉴 키·주소·권한은 하나도 바뀌지 않았다. 옮긴 것은 사이드바에서 어느 줄 아래 서는가뿐이라,
      즐겨찾기도 이미 나눠 둔 권한도 그대로다.
  */
  { menuKey: 'news_guidelines', label: '사내지침&매뉴얼', groupKey: 'board', sortOrder: 3, itemType: 'link', href: '/news/guidelines', searchAliases: ['보미소식', '지침', '매뉴얼', '규정'] },
  { menuKey: 'news_employee', label: '신규입사자 소식', groupKey: 'board', sortOrder: 4, itemType: 'link', href: '/news/employees', searchAliases: ['보미소식', '입사', '퇴사', '신입', '인사이동'] },
  { menuKey: 'news_polls', label: '투표', groupKey: 'board', sortOrder: 5, itemType: 'link', href: '/news/polls', searchAliases: ['보미소식', '투표', '설문', '비밀투표', '찬반', '의견수렴', '네이밍', '이름 공모'] },
  // 건설동향·뉴스는 같은 날 다시 내려갔다 — 홈 위젯 「뉴스·건설동향」의 전체보기가 입구다
  // (위 `RETIRED_MENU_KEYS`).

  /*
    보미기술집 — <b>최상위 링크 하나</b>다 (사용자 지시 2026-09-22).

    <p>나흘 전(2026-09-18) 게시판 안으로 들어가면서 그룹 하나에 하위 셋이 되었다. 그러자 기술집 글에
    닿는 데 사이드바를 두 번 펴야 했고, 게시판 하위는 여섯 줄이 됐다. 지금은 <b>자료실 다음 최상위</b>
    옛 자리로 돌아오면서 하위 셋도 걷었다.

    ★ 공종별 기술집·신기술 소개는 <b>화면 안의 탭</b>이 되었다({@code TechCollectionPage}). 두 화면은
      본디 한 컴포넌트가 `kind` 만 달리해 그리던 것이라, 탭으로 묶는 데 새로 짠 화면이 없다.
      옛 주소(`/tech/cases`·`/tech/new`)는 <b>그대로 열린다</b> — 각 탭이 자기 주소를 갖는다.
    ★ 보미 실적은 <b>현장별 준공 탭</b>으로 갔다(사용자 지시). 실적 한 장이 담는 것은 결국 그 현장의
      준공 기록이라, 현장을 떠나 따로 선 목록이 아니라 그 현장 안에 있어야 한다
      ({@code SitePmisPage} 의 준공 탭 · {@code PerformancePanel}). 준공 현장의 목록은
      현장 현황판의 <b>준공</b> 덩이가 이미 세운다.
    ★ 권한 키 {@code tech_cases}·{@code tech_new} 는 살아 있다 — 탭마다 그 키로 갈린다. 줄이 없으니
      권한 매트릭스에서는 보미기술집 밑에 매단다({@code permissionTree} 의 `ATTACHED_KEYS`).
  */
  { menuKey: 'tech', label: '보미기술집', groupKey: null, sortOrder: 6, itemType: 'link', href: '/tech', visibleWhenAnyOf: ['tech_cases', 'tech_new'], searchAliases: ['기술집', '보미기술집', '공종별 기술집', '신기술 소개', '우수사례', 'VE'] },
  /*
    하위 둘을 <b>되살렸다</b> (사용자 지적 2026-09-22 「보미기술집은 안 됐는데」).

    위 머리말이 이 둘을 걷은 이유는 «사이드바가 길어진다» 였다. 하위 항목이 사이드바에서 펼쳐지던
    때의 이야기다 — 이제 하위는 <b>화면 맨 위의 탭</b>으로 서고({@code MenuTabs}) 사이드바는 한 줄
    그대로다. 걷어 둔 채로 두면 이 화면만 상단 탭이 없어, 다른 메뉴와 다르게 보인다.

    화면 안에서 그리던 탭({@code TechCollectionPage} 의 WorkTabs)은 같은 이름이 두 줄로 서므로
    함께 걷었다. <b>탭은 한 곳에서만 그린다.</b>
  */
  { menuKey: 'tech_cases', label: '공종별 기술집', groupKey: 'tech', sortOrder: 1, itemType: 'link', href: '/tech/cases', searchAliases: ['공종별 기술집', '우수사례'] },
  { menuKey: 'tech_new', label: '신기술 소개', groupKey: 'tech', sortOrder: 2, itemType: 'link', href: '/tech/new', searchAliases: ['신기술', 'VE'] },


  /*
    공지사항 — 옛 「보미소식」 그룹이 있던 첫 자리다 (사용자 지시 2026-09-18: 「보미소식 →
    공지사항으로 하나로만 쓰게 이름을 바꾸자」).

    <p>그룹이 아니라 <b>링크</b>다. 아래 일곱 줄 중 다섯이 게시판으로 내려가고 식당안내가 위젯이
    되면서 남은 것이 이 한 줄뿐인데, 그 상태의 그룹은 「펴야 한 줄이 나오는 자리」다
    (요청게시판을 게시판 안으로 접은 것과 같은 판단 · 2026-09-14).

    ★ 키가 {@code news} 가 아니라 {@code news_notices} 인 이유는 <b>권한</b>이다. 그룹 머리는
      권한 키를 갖지 않아서, `news` 를 링크로 올리면 아무도 가지지 않은 키로 판정돼 전원에게
      안 보인다. 반대로 `news_notices` 는 이미 서버 권한 표에 있는 진짜 키다
      (백엔드 {@code MenuActions} · {@code NoticeBoardKey.menuKey()}).

    <p>옛 이름으로 찾는 사람이 한동안 남으므로 「보미소식」을 별칭에 둔다.
  */
  { menuKey: 'news_notices', label: '공지사항', groupKey: null, sortOrder: 1, itemType: 'link', href: '/news/notices', searchAliases: ['보미소식', '공지', '알림'] },


  { menuKey: 'assets', label: '자산관리', groupKey: null, sortOrder: 7, itemType: 'group' },
  // 자산 공지는 메뉴가 아니다 — 자산관리 화면 맨 위에 붙는다. `asset_notices` 는 그 칸의 권한 키로만 산다.
  // 자산관리는 <b>하러 온 일</b>로 갈린다(2026-08-19) — 빌리러 왔나(예약·신청), 관리하러 왔나(대장).
  // 예전에는 네 화면이 한 줄로 늘어서 있어 신청하러 온 사람이 인증서 대장을 지나쳐야 했다.
  { menuKey: 'asset_requests', label: '예약·신청 관리', groupKey: 'assets', sortOrder: 1, itemType: 'group' },
  // 차량·회의실은 같은 화면을 종류만 고정해 나눠 쓴다. 권한은 예약 키 하나(`asset_reservations`)로 함께 열린다.
  // 「배차」 는 더 이상 이 줄의 별칭이 아니다 — 배차 관리는 총무 관리의 줄이다(2026-10-19).
  { menuKey: 'asset_vehicle_reservations', label: '차량 예약', groupKey: 'asset_requests', sortOrder: 1, itemType: 'link', href: '/assets/reservations/vehicles', visibleWhenAnyOf: ['asset_reservations'], searchAliases: ['차량예약'] },
  { menuKey: 'asset_room_reservations', label: '회의실 예약', groupKey: 'asset_requests', sortOrder: 2, itemType: 'link', href: '/assets/reservations/rooms', visibleWhenAnyOf: ['asset_reservations'], searchAliases: ['회의실', '회의실예약'] },
  { menuKey: 'asset_supplies', label: '소모품·비품 신청', groupKey: 'asset_requests', sortOrder: 3, itemType: 'link', href: '/assets/supplies', searchAliases: ['소모품 불출', '비품신청'] },

  // 경비·퀵서비스도 여기 붙는다(2026-08-19) — 최상위 「경비」 탭을 따로 세우지 않기로 했다.
  // 총무가 <b>대장을 관리하는</b> 일이라는 점에서 자산 대장과 같은 성격이고, 최상위 줄이 하나 늘면
  // 사이드바에서 자산과 경비를 오가며 찾게 된다.
  { menuKey: 'asset_registry', label: '자산·경비 관리', groupKey: 'assets', sortOrder: 2, itemType: 'group' },
  // 비품 자산 현황·직원 자산 현황은 2026-10-19 에 아래 「총무 관리」 로 옮겼다(DOC-120).
  // 현장별 자산 현황(V120).
  { menuKey: 'asset_site_inventory', label: '현장 자산 현황', groupKey: 'asset_registry', sortOrder: 1, itemType: 'link', href: '/assets/site-inventory', searchAliases: ['현장자산', '현장별자산', '자산현황', '가설자재', '현장비품'] },
  { menuKey: 'asset_supplies_admin', label: '소모품 관리', groupKey: 'asset_registry', sortOrder: 2, itemType: 'link', href: '/assets/supplies/items', searchAliases: ['재고', '품목등록'] },
  { menuKey: 'asset_certificates', label: '공인인증서', groupKey: 'asset_registry', sortOrder: 3, itemType: 'link', href: '/assets/certificates', searchAliases: ['인증서', '공동인증서'] },
  { menuKey: 'asset_software', label: '소프트웨어 자산', groupKey: 'asset_registry', sortOrder: 4, itemType: 'link', href: '/assets/software', searchAliases: ['CAD', '오토캐드', '라이센스', '프로그램'] },
  { menuKey: 'asset_movements', label: '자산 이동', groupKey: 'asset_registry', sortOrder: 5, itemType: 'link', href: '/assets/movements', searchAliases: ['창고', '현장반출', '입출고', '이동'] },
  // 잡자재 대장(요청 No.11). 「경비 신청 대장」·「퀵서비스 대장」과 같은 부류라 그 옆에 둔다 —
  // 셋 다 <b>결재가 만든 행을 담당팀이 진행시키는 자리</b>이고, 대장을 찾는 사람이 한 군데만 보면 된다.
  { menuKey: 'asset_material_requests', label: '잡자재 대장', groupKey: 'asset_registry', sortOrder: 6, itemType: 'link', href: '/assets/materials', searchAliases: ['잡자재', '기성첨부', '반영월', '자재신청', '구매'] },
  { menuKey: 'expense_standards', label: '경비 기준안', groupKey: 'asset_registry', sortOrder: 7, itemType: 'link', href: '/expenses/standards', searchAliases: ['부서경비', '현장경비', '기준안', '단가'] },
  { menuKey: 'expense_requests', label: '경비 신청 대장', groupKey: 'asset_registry', sortOrder: 8, itemType: 'link', href: '/expenses/requests', searchAliases: ['경비', '부서경비', '현장경비'] },
  { menuKey: 'expense_courier', label: '퀵서비스 대장', groupKey: 'asset_registry', sortOrder: 9, itemType: 'link', href: '/expenses/courier', searchAliases: ['퀵', '퀵서비스', '오토바이'] },
  // 자재 단가(V143). 「경비 기준안」 과 같은 성격이라 같은 그룹에 둔다 — 둘 다 <b>단가를 정하는 표</b>이고,
  // 단가를 찾는 사람이 사이드바에서 한 군데만 보면 되게 한다. 홈 위젯이 이 표를 읽는다.
  { menuKey: 'asset_material_prices', label: '자재 단가 설정', groupKey: 'asset_registry', sortOrder: 10, itemType: 'link', href: '/assets/material-prices', searchAliases: ['철근', '레미콘', '자재단가', '시세', '환율', '단가'] },
  // 식대 관리(V294). 「경비 신청 대장」 과 같은 부류라 이 그룹이다 — 달마다 숫자를 세어 정산하는
  // 표다. 찍는 화면(`/meals/check`)은 메뉴가 아니다: 식당 QR 로만 닿는 자리라 사이드바에 세우면
  // 「눌러서 체크하는 버튼」 이 되고, 그러면 식당에 가지 않아도 찍힌다.
  { menuKey: 'asset_meals', label: '식대 관리', groupKey: 'asset_registry', sortOrder: 11, itemType: 'link', href: '/assets/meals', searchAliases: ['식대', '중식', '점심', '구내식당', '식수', '급식'] },

  /*
    총무 관리 (사용자 지시 2026-10-19 · DOC-120 · V497) — 「인사총무팀, 공사관리팀만 볼 수 있고 작업할
    수 있게」(공사관리팀 = 공사전략팀의 오기, 같은 날 확인). 자산관리에서 세 줄을 옮겨 왔다.

    <p>그룹 자체에는 권한 키가 없다 — 아래 세 키 중 하나라도 가진 사람에게만 선다(`useMenuTree`).
    두 부서는 V497 이 부서 역할에 심었고, 다른 부서의 줄은 같은 판이 걷었다. 주소(`/assets/…`)는
    그대로다: 앱·릴리즈 노트·안내 대본이 그 주소를 들고 있고, 옮긴 것은 자리이지 화면이 아니다.

    <p>배차 관리는 차량 예약 화면의 탭이었다(2026-10-07). 독립 화면으로 빼면서 키도 새로 받았다
    (`asset_dispatch`) — 예약 키에 매어 두면 「예약은 전원」 과 「배차는 두 부서」 를 한 키로 가를 수 없다.
  */
  { menuKey: 'general_affairs', label: '총무 관리', groupKey: null, sortOrder: 8, itemType: 'group' },
  { menuKey: 'asset_items', label: '비품 자산 현황', groupKey: 'general_affairs', sortOrder: 1, itemType: 'link', href: '/assets/items', searchAliases: ['차량등록', '회의실등록', '자산대장'] },
  // 사람에게 나간 것(V285). 소속만으로는 서지 않는다(`DepartmentBaseline`).
  { menuKey: 'asset_staff', label: '직원 자산 현황', groupKey: 'general_affairs', sortOrder: 2, itemType: 'link', href: '/assets/staff', searchAliases: ['지급자산', '개인자산', '태블릿', '노트북', '직원자산'] },
  { menuKey: 'asset_dispatch', label: '배차 관리', groupKey: 'general_affairs', sortOrder: 3, itemType: 'link', href: '/general-affairs/dispatch', searchAliases: ['배차', '배차관리', '차량 배차', '배차 승인'] },

  /*
    원격지원 (사용자 지시 2026-10-27 · DOC-137 · V573) — 「외부현장에서도 원격으로 우리가 작업해줄 수 있게」.
    MeshCentral 을 우리 서버에 띄우고, 이 화면에서 [연결] 을 누르면 그 PC 화면이 브라우저 새 탭으로 열린다
    (현장은 「BoMI 원격지원」 수락 창에서 [수락]). 2026-10-27 에 RustDesk 로 섰다가 같은 날 바뀌었다 — DOC-137 §1.

    <p>그룹 자체에는 권한 키가 없다 — 아래 키를 가진 사람에게만 선다. 키는 닫아서 냈고(V573) 역할에 심지 않았다.
  */
  { menuKey: 'remote_support', label: '원격지원', groupKey: null, sortOrder: 9, itemType: 'group' },
  { menuKey: 'remote_support_pcs', label: 'PC 원격 접속', groupKey: 'remote_support', sortOrder: 1, itemType: 'link', href: '/remote-support', searchAliases: ['원격', '원격지원', '원격제어', '화면 공유', 'PC 접속', 'MeshCentral'] },

  // 맨 끝. 요청게시판 그룹이 빠지면서 10 으로(2026-09-14), 보미기술집이 게시판 안으로 들어가면서
  // 9 로 당겼다가(2026-09-18), 그 보미기술집이 최상위로 돌아오면서 다시 10 이었고(2026-09-22),
  // 같은 날 전자결재가 헤더로 올라가면서 9 였고, 총무 관리가 서면서(2026-10-19) 10, 안전 관리가 서면서
  // (2026-10-20) 11, 안전 관리가 안전 홈페이지로 떠나면서(2026-10-27) 10, 같은 날 원격지원이 서면서 <b>11</b> 이다 — 번호에 구멍을 남기면 「낯선 최상위 키는 골격 뒤로」 계산
  // ({@code ROOT_MENU_ORDER.length} + n)과 자리가 겹친다.
  { menuKey: 'settings', label: '환경설정', groupKey: null, sortOrder: 11, itemType: 'group' },
  { menuKey: 'settings_departments', label: '부서 관리', groupKey: 'settings', sortOrder: 2, itemType: 'link', href: '/settings/departments' },
  { menuKey: 'settings_sites', label: '현장 관리', groupKey: 'settings', sortOrder: 3, itemType: 'link', href: '/settings/sites' },
  /*
    협력사 마스터(V245~V247 · DOC-82 §2). <b>부서 관리·현장 관리 옆</b>이다 — 셋 다 회사가 하나씩만
    갖는 <b>기준자료</b>이고, 다른 화면들이 그것을 가리켜 쓴다. 자산·경비 묶음에 두지 않은 이유도
    같다: 저기 있는 것들은 결재가 만든 행을 담당팀이 진행시키는 <b>대장</b>이고, 이것은 그 대장이
    가리키는 <b>정본</b>이다.

    ★ 새 최상위 줄을 만들지 않는다 — `ROOT_MENU_ORDER` 는 사용자가 정한 순서라 화면 하나 때문에
      흔들 자리가 아니다(테스트가 그 순서를 지킨다).
    ★ 권한은 환경설정과 함께 가지 않는다. V247 이 이 키를 외주구매팀·공사전략팀·직책 「공무」에게
      주고, 매트릭스에서도 따로 판다(`permissionGroups`) — 사이드바에서 이웃이라는 것과 같은 손에
      맡긴다는 것은 다른 말이다.
  */
  { menuKey: 'vendor_master', label: '협력사 마스터', groupKey: 'settings', sortOrder: 4, itemType: 'link', href: '/vendors', searchAliases: ['협력사', '협력업체', '외주', '하도급업체', '감리', '업체', '거래처', '사업자등록번호'] },
  // 문서 분류 관리와 NAS 접속 설정은 환경설정이 아니라 자료실 아래에 있다(문서 분류 메뉴 · 자료 조회 NAS 탭).
  // settings_nas 는 메뉴가 아니라 'NAS 관리 권한' 키로만 남는다(백엔드 NasConnectionController 가드 · permissionGroups).
  { menuKey: 'settings_menus', label: '메뉴 관리', groupKey: 'settings', sortOrder: 6, itemType: 'link', href: '/settings/menus' },
  { menuKey: 'settings_users', label: '사용자·권한 관리', groupKey: 'settings', sortOrder: 7, itemType: 'link', href: '/settings/users' },
  { menuKey: 'settings_roles', label: '역할·권한 관리', groupKey: 'settings', sortOrder: 8, itemType: 'link', href: '/settings/roles' },
  /*
    권한 진단(사용자 지시 2026-09-16) — 「이 사람은 무엇이 보이고, 왜 그런가」.

    <b>역할·권한 관리 바로 아래</b>다. 판정 축이 일곱이고 고치는 자리가 다섯이라, 「왜 안 보이지」
    의 답을 찾으려면 표를 여러 개 열어 봐야 했다. 이 줄은 <b>보는 자리</b>이고 고치는 자리는
    그대로 남는다.

    ★ 키를 {@code settings_roles} 와 <b>같이 쓰지 않는다</b>. 권한 나무는 키 하나에 줄 하나를
      요구해서(`permissionTree`), 같은 키로 두 줄을 세우면 매트릭스에 같은 칸이 두 번 나오고
      어느 쪽을 껐는지 알 수 없어진다. 대신 V283 이 <b>역할·권한 관리를 이미 보는 역할</b>에게
      이 키를 함께 켜 준다 — 배포한 날 아무도 못 보는 일이 없게.
  */
  { menuKey: 'settings_permission_diagnosis', label: '권한 진단', groupKey: 'settings', sortOrder: 8, itemType: 'link', href: '/settings/permission-diagnosis', searchAliases: ['권한', '진단', '왜 안보임', '권한확인', '누가 보나', '권한점검'] },
  { menuKey: 'settings_backup', label: '데이터 백업', groupKey: 'settings', sortOrder: 8, itemType: 'link', href: '/settings/backup' },
  { menuKey: 'settings_mail', label: '메일 설정', groupKey: 'settings', sortOrder: 9, itemType: 'link', href: '/settings/mail' },
  // 앱 푸시(V222 · V244). 메일 설정 바로 뒤에 둔다 — 「알림이 안 온다」 를 들고 온 사람이 메일과
  // 푸시 두 곳을 사이드바에서 찾아 헤매지 않게 한다.
  { menuKey: 'settings_push', label: '푸시 알림', groupKey: 'settings', sortOrder: 9, itemType: 'link', href: '/settings/push', searchAliases: ['푸시', 'push', '앱알림', 'FCM', '모바일알림', '기기', '토큰'] },
  // 헤더 「바로가기」 드롭다운의 목록(홈페이지·블로그·그룹웨어…). 링크 자체는 메뉴가 아니라 헤더에 산다(Navigation).
  // 외부 API 키(V144). 메일 설정 바로 뒤에 둔다 — 둘 다 <b>바깥과 붙는 설정</b>이고, "연결이 안 된다" 를
  // 들고 오는 사람이 사이드바에서 두 곳을 오가지 않게 한다.
  { menuKey: 'settings_external_api', label: '외부 API 설정', groupKey: 'settings', sortOrder: 9, itemType: 'link', href: '/settings/external-api', searchAliases: ['API키', 'api key', '날씨키', '카카오키', '환율키', '공휴일키', '시크릿', 'secret'] },
  { menuKey: 'settings_shortcuts', label: '바로가기 관리', groupKey: 'settings', sortOrder: 10, itemType: 'link', href: '/settings/shortcuts' },
  // 안전관리 관리자(V604 · DOC-141 §3 · 2026-10-30). 안전 서비스(safe)의 「관리자」 역할을 쥘 직원 — 명부 스냅샷이 실어 간다.
  // 오픈 게이트에 닫아서 낸다.
  { menuKey: 'settings_safety_admins', label: '안전관리 관리자', groupKey: 'settings', sortOrder: 10, itemType: 'link', href: '/settings/safety-admins', searchAliases: ['안전', '안전관리', '안전 관리자', 'safe', '안전보건팀', '협력업체 계정'] },
  // 로그인 화면(2026-09-15). 바로가기 관리 바로 뒤에 둔다 — 둘 다 <b>전 직원이 보는 겉모습</b>을
  // 고치는 자리다(저쪽은 헤더의 링크 목록, 이쪽은 첫 화면의 바탕).
  { menuKey: 'settings_login', label: '로그인 화면', groupKey: 'settings', sortOrder: 10, itemType: 'link', href: '/settings/login-design', searchAliases: ['로그인', '첫화면', '배경', '배경화면', '시안', '디자인', '격자', '시차', '커서'] },
  // 자사 홈페이지(DOC-114 · 2026-10-08). 로그인 화면 바로 뒤 — 둘 다 <b>바깥 사람이 보는 겉모습</b>을
  // 고치는 자리다. 오픈 게이트에 닫아서 낸다.
  { menuKey: 'settings_homepage', label: '홈페이지 관리', groupKey: 'settings', sortOrder: 10, itemType: 'link', href: '/settings/homepage', searchAliases: ['홈페이지', '회사홈페이지', '자사홈페이지', '웹사이트', '시안', '게시글', '시공실적', '문의', '번역', '리다이렉트'] },
  // 퇴사자 설문 양식(V146). 메일 설정 계열 뒤에 둔다 — 자동으로 나가는 메일의 문구를 고치러 온
  // 사람이 두 곳을 오가지 않게 한다. 증명서 서식 관리(인사관리)와는 다루는 것이 다르다:
  // 저쪽은 직원이 받아 가는 공문서와 회사 도장, 이쪽은 이미 떠난 사람에게 한 번 나가는 설문이다.
  { menuKey: 'settings_exit_survey', label: '퇴사자 설문 양식', groupKey: 'settings', sortOrder: 11, itemType: 'link', href: '/settings/exit-survey', searchAliases: ['퇴사', '퇴사자', '설문', '퇴사설문', '퇴직', '이직', '설문지', '문항', '퇴사사유'] },
  // 오류 기록(V406 · 2026-10-07). 서버가 남긴 WARN·ERROR 를 묶어 「누가 무엇을 겪었나」 로 본다.
  // 최고 관리자 전용({@link SUPER_ADMIN_ONLY_MENU_KEYS}) · 오픈 게이트에 닫아서 낸다.
  { menuKey: 'settings_error_log', label: '오류 기록', groupKey: 'settings', sortOrder: 12, itemType: 'link', href: '/settings/error-log', searchAliases: ['오류', '에러', '경고', '로그', 'error', 'log', '장애', '예외'] },
  // 변경이력(V448 · 2026-10-14). 노트가 어느 판에 실려 나갔는지·숨김을 고친다. 2026-10-30(V603)부터 역할로 준다.
  { menuKey: 'settings_release_notes', label: '변경이력', groupKey: 'settings', sortOrder: 13, itemType: 'link', href: '/settings/release-notes', searchAliases: ['변경이력', '릴리즈', '릴리즈노트', '업데이트', '버전', '배포', 'release', 'job'] },
];

const LOCKED_KEYS = new Set<string>(LOCKED_MENU_STRUCTURE_KEYS);

/**
 * <b>순서까지</b> 골격이 이기는 키 — 현장 현황의 셋과, 입찰 아래 갈래 둘이다.
 *
 * <p>{@link LOCKED_MENU_STRUCTURE_KEYS} 는 소속(groupKey)과 종류만 잠근다. 순서는 관리자가
 * 메뉴 관리에서 바꿀 수 있고, 그래서 실제 운영에서 <b>입찰이 진행 위에 서 있었다</b>
 * (사용자 지적 2026-09-29: 「진행 탭이 먼저 나와야 하는데 입찰이 먼저 나오고 있더라」).
 *
 * <p>현장 현황 셋은 현장의 <b>흐름</b>이라 순서 자체가 뜻이다 — 진행 중인 현장이 먼저 오고, 아직
 * 우리 것이 아닌 입찰이 그다음, 끝난 현장이 마지막이다. 그 순서를 화면 설정이 뒤집지 못하게 한다.
 *
 * <p>입찰 아래 둘도 그렇다(2026-09-30). <b>수주가 먼저</b>다 — 일이 그 순서로 온다: 우리가 먼저
 * 수주하고, 그 현장의 공사를 협력사에게 발주한다. 뒤집히면 아직 우리 것도 아닌 현장의 발주가
 * 먼저 서게 된다.
 */
const LOCKED_ORDER_KEYS = new Set<string>([
  'site_status_active',
  'site_status_bid',
  'site_status_completed',
  'site_status_bid_tender',
  'site_pmis_rfq',
  // 주간업무의 첫 칸(2026-10-21). 저장된 순서(형제이던 때의 1)가 이기면 본사와 자리를 다툰다.
  'dashboard',
]);

/** 라벨을 관리자 설정으로 덮어쓰지 않는 키. v1 의 `forceDefaultLabelKeys` 와 같다. */
const FORCE_DEFAULT_LABEL_KEYS = new Set<string>([]);

/**
 * <b>v2 에서 개명한</b> 메뉴. v1 덤프가 실어 온 행(`origin='v1_import'`)의 라벨을 <b>안 쓴다</b> —
 * 골격 라벨이 이긴다. 관리자가 메뉴 관리 화면에서 고친 값(`origin='admin'`)은 그대로 이긴다.
 *
 * <p>왜 필요한가: 개명은 v1 에 없는 v2 의 결정인데 v1 의 옛 이름이 덤프에 실려 온다. DB 를 고쳐 놓는
 * 방식(V69·V89·V111)은 <b>한 번만 도는 마이그레이션</b>이라, 그 뒤에 옛 판 sync 스크립트로 한 번만
 * 덮여도 영영 옛 이름이 남는다 — 「사내지침&매뉴얼」이 2026-08-20·2026-08-24 두 번 그렇게 되돌아갔다.
 * 되돌림을 막는 자리를 화면 쪽에도 둔다. `scripts/v1-to-v2/02_map_to_v2.sql` §7
 * `v2_renamed_menu_labels` 와 <b>같은 목록</b>이어야 한다.
 */
const V2_RENAMED_MENU_LABEL_KEYS = new Set<string>([
  'news_guidelines', // 사내지침 → 사내지침&매뉴얼 (V69 · DOC-43 §13.1)
  'site_status',     // 현장 현황판 → 현장 현황 (V301 · 사용자 지시 2026-09-18)
  'calendar',        // 통합 캘린더 → 전체 (월간업무 하위 · 사용자 지시 2026-10-01)
]);

/**
 * 이 항목을 여는 메뉴 키들 — 자기 키와 대체 키({@link MenuConfigItem.visibleWhenAnyOf}).
 * 사이드바(`useMenuTree`)와 라우트 가드(`lib/auth/access.ts`)가 같은 판정을 쓰도록 여기 한 곳에 둔다.
 */
export function menuKeysOf(item: Pick<MenuConfigItem, 'menuKey' | 'visibleWhenAnyOf'>): string[] {
  return [item.menuKey, ...(item.visibleWhenAnyOf ?? [])];
}

/** 화면 키 → 그 화면을 여는 키들. 라우트 가드가 `menuKey` 문자열만 들고 판정할 때 쓴다. */
export const MENU_KEY_ALIASES: Record<string, string[]> = Object.fromEntries(
  DEFAULT_MENU_CONFIG.filter((item) => item.visibleWhenAnyOf).map((item) => [item.menuKey, menuKeysOf(item)]),
);

/**
 * 기본 골격에 서버 설정을 겹친다.
 *
 * 서버 설정이 비어 있어도(아직 못 받아왔거나 요청이 실패해도) 기본 골격은 그대로 남는다.
 * 메뉴가 통째로 사라져 업무가 멈추는 것보다, 라벨이 잠깐 기본값인 편이 낫다.
 */
export function mergeMenuConfig(menuSettings: MenuSetting[] | null | undefined): MenuConfigItem[] {
  const settingsMap = new Map((menuSettings ?? []).map((item) => [item.menuKey, item]));

  const mergedDefaults = DEFAULT_MENU_CONFIG.map((item) => {
    const saved = settingsMap.get(item.menuKey);
    // v1 덤프가 실어 온 행에서는 **라벨만** 쓴다. 그 행의 순서·배치는 v1 화면의 흔적이지 관리자가
    // v2 에서 정한 값이 아니다 — 그대로 따르면 덤프를 뜰 때마다 사이드바가 v1 순서로 되돌아간다.
    const arranged = saved?.origin === 'v1_import' ? undefined : saved;
    const structureLocked = LOCKED_KEYS.has(item.menuKey);
    return {
      ...item,
      label: FORCE_DEFAULT_LABEL_KEYS.has(item.menuKey)
        ? item.label
        : (V2_RENAMED_MENU_LABEL_KEYS.has(item.menuKey) ? arranged?.label : saved?.label) ?? item.label,
      groupKey: structureLocked ? item.groupKey : arranged?.groupKey ?? item.groupKey,
      sortOrder: LOCKED_ORDER_KEYS.has(item.menuKey) ? item.sortOrder : arranged?.sortOrder ?? item.sortOrder,
      itemType: structureLocked ? item.itemType : arranged?.itemType ?? item.itemType,
    };
  });

  // 관리자가 추가한 메뉴는 기본 골격에 없으므로 href 가 없다. 표시만 되고 이동은 하지 않는다.
  // 물러난 키(화면 통합으로 골격에서 빠진 것)는 여기 섞이면 안 된다 — 이동할 수 없는 옛 메뉴가 되살아난다.
  const customItems = (menuSettings ?? [])
    .filter((item) => !RETIRED_MENU_KEYS.has(item.menuKey))
    .filter((item) => !DEFAULT_MENU_CONFIG.some((defaultItem) => defaultItem.menuKey === item.menuKey))
    .map((item) => ({
      menuKey: item.menuKey,
      label: item.label,
      groupKey: item.groupKey,
      // v1 에서 딸려 온 낯선 최상위 키는 골격의 열 개 <b>뒤</b>로 민다. 그러지 않으면 sort_order 1 을
      // 들고 와 「보미소식」 위에 앉는다 — 관리자가 손댄 적도 없는 메뉴가 첫 줄을 차지한다.
      sortOrder:
        item.groupKey === null && item.origin === 'v1_import'
          ? ROOT_MENU_ORDER.length + item.sortOrder
          : item.sortOrder,
      itemType: item.itemType,
      href: undefined,
    }));

  return [...mergedDefaults, ...customItems];
}
